/**
 * Theme presets — named bundles of theme-token values the seller can apply in
 * one click from the editor's theme inspector. Presets only touch theme
 * tokens (colors / typography / radius / logoTreatment); they never modify
 * section content.
 */
/**
 * The `eo-*` presets reproduce one EasyOrders theme ENGINE each (13 engines
 * cover their whole 69-store gallery — see
 * docs/plans/2026-07-19-storefront-section-vocabulary.md). Each pins the
 * engine's palette, Latin typeface, corner radius and ported product card, so
 * applying one gets a storefront that reads as that theme. Section layout per
 * engine is layered on top separately.
 */
/**
 * One `ps-featured` product row bound to the store's Nth category.
 *
 * The heading treatment is the measured `ps-featured` one (28px w400,
 * `letter-spacing: 5.04px`, uppercase, centred = `titleStyle: "spaced"` +
 * `titleAlign: "center"`) — the `elite, avyro, nature, sharp` row of the
 * featured-products table in the plan doc.
 *
 * `title` is left null on purpose: `category-sections` falls back to the
 * category's own name, which is the seller's word for it in both languages.
 * Hard-coding "Men Hoodies" would ship avyro's catalogue into every store.
 *
 * ## The four rows are NOT the same treatment
 *
 * This is the single biggest thing the page-level diff caught, and no
 * per-section comparison could have: avyro ships its four category rows in
 * THREE different layouts, and the plan doc's earlier note ("theirs is a scroll
 * track with a progress rail; ours is a static grid") described only the first
 * one. Measured on avyro-elite at 1440, section heights 658 / 1017 / 1292 /
 * 1348:
 *
 * | row | their layout | ours |
 * |---|---|---|
 * | 1 Men Hoodies | scroll track, 3 cards visible (405px), progress rail + round prev/next | `slider`, `columnsPerRow: 3` |
 * | 2 Men Sweatpants | 4×2 grid whose FIRST CELL is a category banner tile carrying the name and a "View All" button | `banner-grid` |
 * | 3 Women Hoodies | scroll track, 2 cards visible (~740px), same rail | `slider`, `columnsPerRow: 2` |
 * | 4 Women Sweatpants | plain 4×2 grid with a centred "VIEW ALL →" button beneath | `standard` |
 *
 * Shipping one treatment for all four is what produced the earlier 1031px-for-
 * everything render: uniform where their page alternates. The alternation is
 * part of the theme's rhythm, not seller noise — a scroller, a banner grid, a
 * scroller, a grid.
 */
function categoryRow(n, slot, treatment) {
    return {
        id: `category-sections-${n}`,
        type: "category-sections",
        visible: true,
        settings: {
            variant: treatment.variant,
            categoryId: `$category:${slot}`,
            title: { ar: null, en: null },
            titleStyle: "spaced",
            titleAlign: "center",
            showSectionTitle: true,
            // `banner-grid` puts the banner in the first cell; the other two have no
            // banner. Left ON only where the variant uses it so the seller's first
            // sight of the row is the layout the theme intends.
            ...(treatment.variant === "banner-grid"
                ? {
                    showBanner: true,
                    bannerImage: null,
                    bannerEyebrow: { ar: null, en: null },
                    bannerTitle: { ar: null, en: null },
                    bannerSubtitle: { ar: null, en: null },
                    bannerCtaLabel: { ar: "عرض الكل", en: "View all" },
                }
                : {}),
            // `columnsPerRow` is how many are VISIBLE across the row. MEASURED per
            // row rather than averaged: taking the dominant value (4) and applying it
            // everywhere is what hid the alternation above.
            columnsPerRow: treatment.columnsPerRow,
            // MEASURED on avyro-elite (2026-07-20). Counted as unique
            // `a[href*="/products/"]` targets, not as track children — these rows
            // nest flex inside flex, and reading the track's own children returns 8
            // phantom "cards" 708px wide.
            //
            // SEVEN on the banner row, eight elsewhere: the banner occupies the first
            // grid cell, so 7 products fill exactly two rows of four the way theirs
            // does. Leaving it at 8 spilled a ninth cell onto a third row and made
            // the section 1457px against their 1017 — a 44% overshoot that only the
            // page-level height diff exposed.
            productsPerSection: treatment.variant === "banner-grid" ? 7 : 8,
            headingLevel: 2,
        },
    };
}
/**
 * One `ps-featured` row on the `nature` page, bound to the store's Nth
 * category. Nature ships TWO of these (#6 Lipstick, #8 Accessories) and they
 * are identical, so they come from one helper — but the family's other two
 * category rows are deliberately NOT built from it: #3 is the split showcase
 * and #12 the `ab-pgrid`, and all three carry different headings (measured
 * 32/w700/start, 28/w400/ls 5.04/uppercase/centre, 26/w700/start).
 */
/**
 * One category row on the `sharp` page, bound to the store's Nth category.
 *
 * Sharp gets its own helper because its HEADING TREATMENT differs from every
 * family built so far, and reusing `categoryRow` would have shipped the wrong
 * one silently. MEASURED on sharp-bags at 1440, all five rows:
 * `24px / w400 / letter-spacing normal / capitalize`, aligned to the START —
 * against the `28px / w400 / ls 5.04px / UPPERCASE / centred` treatment
 * (`spaced` + `center`) that avyro, elite and nature all use. Nearest of our
 * five `TITLE_STYLES` is `plain` (w400, `.28px` tracking, no transform); see
 * the plan doc for the one recorded difference this leaves (`plain` clamps to
 * 28px at 1440 against their flat 24px).
 *
 * Unlike avyro, sharp does NOT alternate a banner into every other row — it
 * runs slider / grid / banner-grid / slider / banner-grid, which is why the
 * variant is a parameter rather than derived from the index.
 */
function sharpRow(n, slot, variant) {
    return {
        id: `category-sections-${n}`,
        type: "category-sections",
        visible: true,
        settings: {
            variant,
            categoryId: `$category:${slot}`,
            title: { ar: null, en: null },
            titleStyle: "plain",
            titleAlign: "start",
            showSectionTitle: true,
            ...(variant === "banner-grid"
                ? {
                    showBanner: true,
                    bannerImage: null,
                    bannerEyebrow: { ar: null, en: null },
                    bannerTitle: { ar: null, en: null },
                    bannerSubtitle: { ar: null, en: null },
                    bannerCtaLabel: { ar: "عرض الكل", en: "View all" },
                }
                : {}),
            // MEASURED per row: 4 across on every one of the five, sliders included
            // (their card is 360px in a 1440 viewport).
            columnsPerRow: 4,
            // SEVEN on the two banner rows — counted on their page as unique
            // `a[href*="/products/"]`: 7 products + 1 banner cell = the 4+4 grid
            // theirs renders. Eight elsewhere. This is the exact trap the avyro
            // assembly recorded, re-measured here rather than assumed.
            productsPerSection: variant === "banner-grid" ? 7 : 8,
            headingLevel: 2,
        },
    };
}
function natureFeaturedRow(n, slot) {
    return {
        id: `category-sections-${n}`,
        type: "category-sections",
        visible: true,
        settings: {
            variant: "banner-grid",
            categoryId: `$category:${slot}`,
            title: { ar: null, en: null },
            titleStyle: "spaced",
            titleAlign: "center",
            showSectionTitle: true,
            showBanner: true,
            bannerImage: null,
            bannerEyebrow: { ar: null, en: null },
            bannerTitle: { ar: null, en: null },
            bannerSubtitle: { ar: null, en: null },
            bannerCtaLabel: { ar: "عرض الكل", en: "View all" },
            columnsPerRow: 4,
            // SEVEN, not eight: the banner takes the first grid cell, so 7 products
            // fill exactly two rows of four the way theirs does.
            productsPerSection: 7,
            headingLevel: 2,
        },
    };
}
/**
 * One product row on the `dark` page, bound to the store's Nth category.
 *
 * Dark needs its own helper because it runs TWO heading treatments on one
 * page, which no family before it did. MEASURED on dark-electronics at 1440:
 *
 *   `ab-plist` + `ab-pgrid` (rows 1–4)  40px / w700 / ls 3.2px / CENTRED
 *   `ab-featured-products`  (row 5)     28px / w400 / ls .28px / START
 *
 * Only the second is in our vocabulary (`plain`, which the TITLE_STYLES table
 * already credits to this family — that note was taken from the featured row
 * alone and is right about it and wrong about the other four). The 40/w700
 * treatment maps to `bold`, our nearest at 32/w800; the residual difference is
 * recorded in the plan doc rather than papered over.
 *
 * `productsPerSection` is passed in because their five rows carry DIFFERENT
 * counts — 8/7/7/8/8, counted as unique `a[href*="/products/"]` targets, not
 * as track children.
 */
function darkRow(n, slot, variant, products) {
    const featured = variant === "banner-grid";
    return {
        id: `category-sections-${n}`,
        type: "category-sections",
        visible: true,
        settings: {
            variant,
            categoryId: `$category:${slot}`,
            title: { ar: null, en: null },
            titleStyle: featured ? "plain" : "bold",
            titleAlign: featured ? "start" : "center",
            showSectionTitle: true,
            ...(featured
                ? {
                    showBanner: true,
                    bannerImage: null,
                    bannerEyebrow: { ar: null, en: null },
                    bannerTitle: { ar: null, en: null },
                    bannerSubtitle: { ar: null, en: null },
                    bannerCtaLabel: { ar: "عرض الكل", en: "View all" },
                }
                : {}),
            // MEASURED: their cards are 311–327px wide in a 1376px content column,
            // i.e. four across on every row.
            columnsPerRow: 4,
            productsPerSection: products,
            headingLevel: 2,
        },
    };
}
/**
 * One `ab-marquee-section` band. Dark ships two — one travelling each way.
 * Their instance is `--mq-font-size-dt: 48px` (= `medium`), `--mq-speed-dt:
 * 35s` (= `medium`) and `--mq-tilt: 0deg` (= `none`).
 */
function marqueeBand(n, direction) {
    return {
        id: `marquee-band-${n}`,
        type: "marquee-band",
        visible: true,
        settings: {
            // MEASURED: their band is `--mq-bg: #0B0C10`, which is this preset's
            // `accent` token. Without this the section inherits `--section-bg:
            // var(--paper-2)` from the wrapper — the wrapper ALWAYS defines that
            // variable, so a renderer-level fallback can never apply — and the band
            // rendered black-on-white against their lime-on-near-black. Caught by
            // LOOKING at the screenshot; it measured the correct height throughout.
            surface: "accent",
            size: "medium",
            speed: "medium",
            direction,
            tilt: "none",
            separator: "dot",
        },
        blocks: [
            {
                id: `marquee-line-${n}-1`,
                type: "marquee-line",
                settings: { text: { ar: null, en: null } },
            },
        ],
    };
}
/**
 * One `home_banner` — a plain full-width promotional image with no copy over
 * it. Theirs run 462px for the two mid-page pair and 1344 (a 1:1 square) for
 * the one below the scroll transition; the height is entirely the artwork's
 * aspect, so nothing here encodes it.
 */
function darkBanner(n) {
    return {
        id: `banner-${n}`,
        type: "banner",
        visible: true,
        settings: {
            variant: "banner-classic",
            headline: { ar: null, en: null },
            subhead: { ar: null, en: null },
            ctaLabel: { ar: null, en: null },
            // `banner.ctaHref` is an ENUM (`all_products | collections | none`), not
            // a URL — an empty string fails the save with `invalid_option`. Their
            // banners are a bare linked image with no button, so `none` is both
            // valid and correct. (Same class of error as avyro's `autoplayInterval`
            // milliseconds, and caught the same way: by running the preset's theme
            // JSON through `validateThemeJson` before applying it.)
            ctaHref: "none",
            image: null,
            // Their banners carry no text at all, so there is nothing to keep
            // readable and a scrim would only mute the artwork.
            overlayOpacity: 0,
            textAlign: "center",
            headingLevel: 2,
        },
    };
}
/**
 * One volta product row.
 *
 * The row treatments are NOT uniform, and the difference is measured rather
 * than assumed (volta-bags @1440):
 *
 * | their block | title box | our mapping |
 * |---|---|---|
 * | `ab-plist` ×2 | x=48, `text-align: left`, header `space-between` with "View All" + prev/next on the far side | `slider`, `titleAlign: "start"` |
 * | `ab-pgrid` | x=476 in a 1344 header, `text-align: center`, `letter-spacing: 3.2px` | `standard`, `titleAlign: "center"` |
 * | `ab-featured-products` | eyebrow + title inside the banner cell | `banner-grid`, `titleAlign: "start"` |
 *
 * Copying dark's helper wholesale would have centred both sliders — dark's own
 * rows ARE centred, so the value is family-specific and not a shared default.
 *
 * All three run four cards across a 1344–1440 content column (311px in the
 * carousels, 322.5px in the grid), so `columnsPerRow: 4` throughout.
 */
/**
 * One hype product row.
 *
 * MEASURED on hype-books @1440 — the four rows are three treatments, and the
 * difference is read off the boxes, not assumed:
 *
 * | their block | title | our mapping |
 * |---|---|---|
 * | `ab-pgrid` "Best Sellers" | 42px/w500, `text-align: center`, 10 cards in a 4-column grid | `standard`, `titleAlign: "center"` |
 * | `ab-plist` ×2 | 42px/w500, `text-align: left`, 6 cards on a 2026px track in a 1392px frame | `slider`, `titleAlign: "start"` |
 * | `ab-featured-products--rows` | NO heading of its own — a 1392x412 banner carries a 48px/w400 capitalised title, then 6 landscape row-cards in 2 columns | `rows`, `showSectionTitle: false` + `showBanner` |
 *
 * `titleStyle: "display"` throughout. There is NO exact stop for the rows'
 * 42px/w500: `display` is `clamp(28px, 3.4vw, 48px)`/w400 with `-.96px`
 * tracking and `capitalize`, `bold` is 32/w800. `display` is much the closer
 * of the two on size and weight, and it is the EXACT match for the
 * `rows` banner title (measured 48px/w400/ls -0.96px/capitalize — that stop
 * was ported from this very engine). The added `capitalize` on the two
 * carousels is a real, recorded difference.
 */
/**
 * One of hype's two identical marquee bands. They bracket the category rail —
 * #1 above it, #3 below it — and are byte-identical apart from an
 * `ab-marquee--arabic` class on the second, which selects an Arabic face and
 * changes nothing about the geometry.
 *
 * `speed: "medium"` is 35s against their measured `--mq-speed-dt: 30s`. The
 * ladder has no 30s rung and the nearest are 35 and 22, so this is ~14% slower
 * than theirs. Recorded rather than papered over with a sixth speed stop for
 * one family.
 */
function hypeMarquee(n) {
    return {
        id: `marquee-band-${n}`,
        type: "marquee-band",
        visible: true,
        settings: {
            surface: "accent",
            size: "medium",
            speed: "medium",
            direction: "start",
            tilt: "none",
            separator: "none",
        },
        blocks: [
            {
                id: `marquee-line-${n}-1`,
                type: "marquee-line",
                settings: { text: { ar: null, en: null } },
            },
        ],
    };
}
/**
 * blast's four product rows. Three heading treatments and two banner states
 * across four rows, so the differences are parameters rather than four
 * hand-written blocks.
 *
 * MEASURED on blast-books at 1440: `ab-plist-title` is 32px/w800 START-aligned
 * with "View All" opposite; `ab-pgrid-title` is 28px/w700 CENTRED; and
 * `ab-featured-products` prints no heading at all — its title lives inside the
 * banner.
 */
/**
 * One prime product row. MEASURED on prime-books: their two `ab-plist`
 * carousels and the `ab-pgrid` grid all print the category's own name as the
 * heading, and the `ab-featured-products` scroller prints it twice — once as
 * the section heading and once inside its leading tile, which is theirs, not
 * a bug of ours to avoid.
 *
 * `titleStyle` is fixed at `plain` for every row: the renderer's own table
 * credits `plain` (28px w400, letter-spacing .28px, left) to "dark, volta,
 * prime, pearl, peral", and prime's row titles measure exactly that.
 */
function primeRow(n, slot, variant, products, titleAlign, showSectionTitle) {
    const bannerLed = variant === "hero-split";
    return {
        id: `category-sections-${n}`,
        type: "category-sections",
        visible: true,
        settings: {
            variant,
            categoryId: `$category:${slot}`,
            // Null on purpose — `category-sections` falls back to the category's own
            // name, which is the seller's word for it and is what theirs prints.
            title: { ar: null, en: null },
            titleStyle: "plain",
            titleAlign,
            showSectionTitle,
            ...(bannerLed
                ? {
                    showBanner: true,
                    bannerImage: null,
                    bannerEyebrow: { ar: null, en: null },
                    bannerTitle: { ar: null, en: null },
                    bannerSubtitle: { ar: null, en: null },
                    bannerCtaLabel: { ar: null, en: null },
                }
                : {}),
            // MEASURED: `ab-pgrid` is 4 columns at 1440. The carousels and the
            // hero-split scroller size their own cells from a percentage basis and
            // ignore this.
            columnsPerRow: 4,
            productsPerSection: products,
            headingLevel: 2,
        },
    };
}
function blastRow(n, slot, variant, products, titleStyle, titleAlign, showSectionTitle) {
    const bannerLed = variant === "hero-subtitle";
    return {
        id: `category-sections-${n}`,
        type: "category-sections",
        visible: true,
        settings: {
            variant,
            categoryId: `$category:${slot}`,
            // Null on purpose — `category-sections` falls back to the category's own
            // name, which is the seller's word for it. Theirs prints the category
            // name on three of the four rows for exactly the same reason, and the
            // fourth prints it inside the banner.
            title: { ar: null, en: null },
            titleStyle,
            titleAlign,
            showSectionTitle,
            ...(bannerLed
                ? {
                    showBanner: true,
                    bannerImage: null,
                    bannerEyebrow: { ar: null, en: null },
                    bannerTitle: { ar: null, en: null },
                    bannerSubtitle: { ar: null, en: null },
                    bannerCtaLabel: { ar: null, en: null },
                }
                : {}),
            // MEASURED: `ab-pgrid` is 4 columns of 321px in a 1336 frame; the two
            // `ab-plist` tracks show 4 cards at a time; `ab-featured-grid` is 3
            // columns of 288px beside the banner.
            columnsPerRow: bannerLed ? 3 : 4,
            productsPerSection: products,
            headingLevel: 2,
        },
    };
}
function hypeRow(n, slot, variant, products, titleAlign) {
    const bannerLed = variant === "rows";
    return {
        id: `category-sections-${n}`,
        type: "category-sections",
        visible: true,
        settings: {
            variant,
            categoryId: `$category:${slot}`,
            // Null on purpose — `category-sections` falls back to the category's own
            // name, which is the seller's word for it. Theirs prints the category
            // name on three of the four rows for exactly the same reason.
            title: { ar: null, en: null },
            titleStyle: "display",
            titleAlign,
            // The `--rows` block prints no heading ABOVE itself: its title is set
            // over the banner photograph, centred. Drawing both would double it —
            // the same trap volta's `banner-grid` row recorded.
            showSectionTitle: !bannerLed,
            ...(bannerLed
                ? {
                    showBanner: true,
                    bannerImage: null,
                    bannerEyebrow: { ar: null, en: null },
                    bannerTitle: { ar: null, en: null },
                    bannerSubtitle: { ar: null, en: null },
                    bannerCtaLabel: { ar: null, en: null },
                }
                : {}),
            // MEASURED: the `ab-pgrid` grid is 4 columns (341px cards in a 1392
            // frame) and both `ab-plist` tracks show 4 cards of 321px at a time.
            columnsPerRow: 4,
            productsPerSection: products,
            headingLevel: 2,
        },
    };
}
function voltaRow(n, slot, variant, products, titleAlign) {
    const featured = variant === "banner-grid";
    return {
        id: `category-sections-${n}`,
        type: "category-sections",
        visible: true,
        settings: {
            variant,
            categoryId: `$category:${slot}`,
            // Left null on purpose: `category-sections` falls back to the category's
            // own name in both languages, which is the seller's word for it.
            title: { ar: null, en: null },
            // MEASURED: 40px / weight 700 on every row — `bold`, not `spaced`
            // (spaced is the 28px w400 ls-5px treatment the ps engines use).
            titleStyle: "bold",
            titleAlign,
            // The `ab-featured-products` row prints NO heading above itself — its
            // eyebrow and title live inside the banner cell, bottom-left, with the
            // "VIEW ALL" link under them. Ours was drawing both, and the duplicate
            // heading was the whole of that row's +20% at 1440. MEASURED: their
            // section is 500px with no header band; the other three rows DO carry a
            // heading above the cards.
            showSectionTitle: !featured,
            ...(featured
                ? {
                    showBanner: true,
                    bannerImage: null,
                    bannerEyebrow: { ar: null, en: null },
                    bannerTitle: { ar: null, en: null },
                    bannerSubtitle: { ar: null, en: null },
                    bannerCtaLabel: { ar: "عرض الكل", en: "View all" },
                }
                : {}),
            columnsPerRow: 4,
            productsPerSection: products,
            headingLevel: 2,
        },
    };
}
/**
 * pearl's three `ab-plist` carousels (#4 Hand Bags, #6 Crossbody Bags,
 * #8 Shoulder Bags). All three are byte-identical apart from the category, so
 * they are generated rather than repeated — the same call volta's row helper
 * made.
 *
 * MEASURED on pearl-bags: each section is 572px at 1440 and 448px at 390; the
 * track is a scroller (`overflow-x: auto`, scrollWidth 2076 against a 1392
 * client, gap 12) whose cards are `calc(25% - 12px)`, so six are authored and
 * about four are visible; the title is 24px weight 200 in navy on the start
 * edge with "View All" opposite.
 *
 * `titleStyle: "plain"` because none of the five treatments is extra-light —
 * `bold` (volta's 40px w700) and `spaced` (the ps engines' 28px w400 ls-5px)
 * are both further away than plain is. Recorded as a departure rather than
 * adding a sixth treatment for one family.
 */
function pearlRow(n, slot) {
    return {
        id: `category-section-${n}`,
        type: "category-sections",
        visible: true,
        settings: {
            variant: "slider",
            categoryId: `$category:${slot}`,
            // Left null on purpose: `category-sections` falls back to the category's
            // own name in both languages, which is what theirs prints too.
            title: { ar: null, en: null },
            showSectionTitle: true,
            showViewAll: true,
            productsPerSection: 6,
            titleStyle: "plain",
            titleAlign: "start",
            headingLevel: 2,
        },
    };
}
const RAW_THEME_PRESETS = [
    {
        id: "default",
        name: { ar: "افتراضي", en: "Default" },
        description: {
            ar: "البرتقالي الكهرماني مع الكحلي الداكن — الألوان الرسمية للمنصة.",
            en: "Amber orange on deep navy — the platform signature palette.",
        },
        tokens: {
            colors: {
                primary: "#F5A524",
                accent: "#0B1220",
                surface: "#F7F4EE",
                ink: "#0B1220",
            },
            typography: { heading: "Cairo", body: "Cairo" },
            radius: "0.5rem",
            logoTreatment: "circle",
        },
    },
    {
        id: "blush",
        name: { ar: "وردي", en: "Blush" },
        description: {
            ar: "بلاطات رمادية ناعمة مع اسم القسم فوق اسم المنتج.",
            en: "Soft grey tiles with a category label above each product name.",
        },
        tokens: {
            colors: {
                // MEASURED on nature-cosmetics (2026-07-20). Their `--color-primary`
                // computes to `0, 0, 0` and every button on the page (trust pill ink,
                // hot-card CTA, split-showcase CTA, FAQ CTA) is `#111111`.
                primary: "#111111",
                // WAS `#e32c2b` — the same unmeasured sale red the avyro note flagged
                // across ten `eo-*` presets, and it appears NOWHERE on their page.
                // `accent` is a BAND colour in our model: it grounds the header and
                // every accent band, `footer-directory` included. Nature's band is the
                // footer ground, authored `--ft-bg: #EED4CD` and computing to
                // rgb(238,212,205), with `--ft-text: #CF5E61` over it.
                accent: "#eed4cd",
                // Their body ground is plain white; `#f7f7f7` is the PRODUCT CARD's
                // ground (`SoftTileCard` hardcodes it, correctly, as part of that
                // card's design) and was mistaken for the page surface.
                surface: "#ffffff",
                // Their body ink computes to rgb(19,19,22); headings to rgb(17,17,17).
                // Kept at the body value — the 2-unit difference is imperceptible.
                ink: "#131316",
                // FIX PHASE B — the FOOTER BAND. The audit found 10 of 12 families on
                // the wrong footer hex because every archetype hard-bound the band to
                // a shared role (`accent` / ink / alt). MEASURED per family.
                // Their band is a PALE PINK and ours painted --fg-on-dark white over it (1.4:1, illegible). Ink is DERIVED from the band; headings are their rose #CF5E61.
                footerSurface: "#eed4cd",
                footerHeading: "#CF5E61",
            },
            // Their heading and body both compute to `"DM Sans", ...` — already
            // correct, re-verified on this pass at 1440.
            typography: {
                heading: "Cairo",
                body: "Cairo",
                headingEn: "DM Sans",
                bodyEn: "DM Sans",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "System Serif",
            },
            radius: "1.25rem",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "16px",
            pageGutterMobile: "16px",
            // `soft-tile` is CORRECT and was already right: `SoftTileCard` is
            // documented in its own header as "a faithful, pixel-level port of the
            // EasyOrders 'Nature Glasses' product card (`ps-pgrid-card`)" — THIS
            // family's card. Measured on nature-cosmetics: radius 20, #f7f7f7
            // ground, category label above a 17/21.25 w700 name, sale price first.
            productCardStyle: "soft-tile",
            productCardRadius: "20px",
        },
        // The THIRD preset to carry page structure (2026-07-20, family 3 of 13,
        // and the deepest at SEVENTEEN sections). Section order, variants and
        // settings are the measured nature-cosmetics homepage — see the "nature"
        // subsection of
        // docs/plans/2026-07-19-storefront-section-vocabulary.md.
        //
        // Conforms to the model `eo-avyro` settled and `eo-elite` confirmed: same
        // `TemplateInstance` shape, same `$category:N` / `$products:N` slots, same
        // rules (no imagery ever, copy is neutral placeholder because text is a
        // CONTENT field the seller's own copy is merged over on apply).
        template: {
            home: {
                sections: [
                    // MEASURED: nature's header is rgba(255,255,255,.96) with #B96F5A
                    // ink, over a white page. Our header field defaults to the ACCENT
                    // band, and this preset's accent is their pale-pink footer ground —
                    // leaving it unset would paint a pink header under a white page.
                    {
                        id: "header",
                        type: "header",
                        locked: true,
                        visible: true,
                        settings: { variant: "aisle", surface: "page", mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false },
                    },
                    // #0 — `ps-slider--under-nav`: the same peek carousel avyro ships
                    // (the plan doc's `ps-slider` hero engine, ported as `spotlight`).
                    // THREE slides here, not avyro's four — their pager reads "1 / 3".
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "spotlight",
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            // SECONDS, not milliseconds — the schema caps this at 2–10 and
                            // the validator rejects anything else.
                            autoplayInterval: 5,
                            textAlign: "start",
                            overlayOpacity: 0,
                        },
                        blocks: [1, 2, 3].map((n) => ({
                            id: `hero-slide-${n}`,
                            type: "slide",
                            settings: {
                                image: null,
                                headline: { ar: null, en: null },
                                subhead: { ar: null, en: null },
                                ctaLabel: { ar: null, en: null },
                                ctaHref: "",
                            },
                        })),
                    },
                    // #1 — `category-section-wrapper`: the heading rail. This is the
                    // engine `rail` was ported FROM, so the family gets its own layout.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "rail",
                            eyebrow: { ar: "اعتني ببشرتك", en: "Care for your skin" },
                            title: {
                                ar: "منتجات عناية طبيعية",
                                en: "Natural self care products",
                            },
                            description: { ar: null, en: null },
                            headingLevel: 2,
                        },
                    },
                    // #2 — `ps-hti`: the headline with photo pills set into the line.
                    // Ported as a NEW section type for this family (nothing we shipped
                    // could interleave media into a sentence).
                    {
                        id: "highlight-headline-1",
                        type: "highlight-headline",
                        visible: true,
                        settings: { align: "center", headingLevel: 2 },
                        blocks: [
                            {
                                id: "hh-a",
                                type: "headline-segment",
                                settings: { kind: "text", text: { ar: "خلّيكي", en: "Make you look" } },
                            },
                            {
                                id: "hh-b",
                                type: "headline-segment",
                                settings: { kind: "pill", image: null, href: "", alt: { ar: null, en: null } },
                            },
                            {
                                id: "hh-c",
                                type: "headline-segment",
                                settings: { kind: "text", text: { ar: "وحاسّة", en: "and feel glowy" } },
                            },
                            {
                                id: "hh-d",
                                type: "headline-segment",
                                settings: { kind: "pill", image: null, href: "", alt: { ar: null, en: null } },
                            },
                            {
                                id: "hh-e",
                                type: "headline-segment",
                                settings: { kind: "text", text: { ar: "بصحة", en: "and healthy" } },
                            },
                            {
                                id: "hh-f",
                                type: "headline-segment",
                                settings: { kind: "pill", image: null, href: "", alt: { ar: null, en: null } },
                            },
                        ],
                    },
                    // #3 — `ps-plist--split-showcase`. MEASURED heading: 32px w700
                    // ls -0.64px, START aligned — `bold` + `start`, NOT the `spaced`
                    // centred treatment the two `ps-featured` rows below use. The three
                    // category rows on this page carry THREE different headings, and
                    // shipping one for all of them is the mistake the avyro assembly
                    // recorded.
                    {
                        id: "category-sections-1",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "split-showcase",
                            categoryId: "$category:0",
                            title: { ar: null, en: null },
                            titleStyle: "bold",
                            titleAlign: "start",
                            showSectionTitle: true,
                            productsPerSection: 8,
                            // The split showcase shows ONE slide at a time; `columnsPerRow`
                            // does not reach it. The schema floor is 2, so 2 it is —
                            // recorded rather than silently defaulted.
                            columnsPerRow: 2,
                            headingLevel: 2,
                        },
                    },
                    // #4 — `psc-compare --aspect-cinematic`. MEASURED 1408x738 at 1440
                    // and 736x386 at 768 → ratio 1.907, which is our existing
                    // `panorama` (21/11 = 1.909). Their `cinematic` modifier has NO
                    // rule in their stylesheet, so it falls through to the engine
                    // default; no new aspect option was needed.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "panel",
                            aspect: "panorama",
                            eyebrow: { ar: "لو داليا", en: "Le Dalia" },
                            title: { ar: "شوف الفرق", en: "See the difference" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    // #5 — `ps-trust-pills`: a centred wrap of outlined pills, each a
                    // check glyph plus a label. Our `chips` value-strip variant is that
                    // shape, so this is a mapping, not a port. Unlike avyro and elite,
                    // nature SHIPS a trust strip, so the section is visible here rather
                    // than present-and-off to suppress the synthetic one.
                    {
                        id: "value-strip-1",
                        type: "value-strip",
                        visible: true,
                        settings: { variant: "chips", showDivider: false },
                        blocks: [
                            ["truck", "شحن مجاني", "Free shipping"],
                            ["shield", "ضمان استرداد", "Money guarantee"],
                            ["card", "دفع مرن", "Flexible payment"],
                            ["headset", "دعم أونلاين", "Online support"],
                            ["returns", "إرجاع خلال ٧ أيام", "Return within 7 days"],
                        ].map(([icon, ar, en], i) => ({
                            id: `vs-${i}`,
                            type: "value-item",
                            settings: { icon, title: { ar, en }, subtitle: { ar: null, en: null } },
                        })),
                    },
                    // #6 / #8 — two `ps-featured` rows. MEASURED heading: 28px w400
                    // ls 5.04px UPPERCASE centred = `spaced` + `center`, the
                    // elite/avyro/nature/sharp row of the featured-products table.
                    // SEVEN products, not eight: the banner occupies the first grid
                    // cell, so 7 fills exactly two rows of four the way theirs does.
                    natureFeaturedRow(2, 1),
                    // #7 — `ps-split-duo-hero`: the rounded two-tone panel. Ported as
                    // the `about-duo` variant. Their photo is on the LEADING edge
                    // (`order: 1` on the visual).
                    {
                        id: "about-1",
                        type: "about",
                        visible: true,
                        settings: {
                            variant: "about-duo",
                            eyebrow: { ar: "لطيف وآمن", en: "Gentle and safe" },
                            title: { ar: "لأصحاب البشرة الحساسة", en: "Made for sensitive skin" },
                            body: { ar: null, en: null },
                            image: null,
                            imagePosition: "start",
                            ctaLabel: { ar: "تسوق الآن", en: "Shop now" },
                            ctaHref: "",
                            headingLevel: 2,
                        },
                    },
                    natureFeaturedRow(3, 2),
                    // #9 — `collection-ticker--marquee`: circular category thumbs
                    // scrolling continuously. A `collections` layout, not an
                    // announcement bar — its content model is the store's categories
                    // and every item is a link to one.
                    {
                        id: "collections-2",
                        type: "collections",
                        visible: true,
                        settings: { variant: "ticker", title: { ar: null, en: null }, headingLevel: 2 },
                    },
                    // #10 — `faq-split-showcase`: intro column, portrait photo,
                    // accordion. Ported as the `faq-showcase` variant.
                    {
                        id: "faq-1",
                        type: "faq",
                        visible: true,
                        settings: {
                            variant: "faq-showcase",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "احنا بنجاوب!", en: "We're answerable!" },
                            subtitle: { ar: null, en: null },
                            image: null,
                            ctaLabel: { ar: "اعرف أكتر", en: "Learn more" },
                            ctaHref: "",
                            headingLevel: 2,
                        },
                    },
                    // #11 — `ps-hot-cards--slider`: the promo-card peek carousel.
                    // The THIRD `collections` instance on this page, which is why the
                    // section's `maxPerTemplate` was raised from 1 to 3.
                    {
                        id: "collections-3",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "hot-cards",
                            title: { ar: "الأكثر رواجًا", en: "Hot this week" },
                            eyebrow: { ar: null, en: null },
                            description: { ar: null, en: null },
                            headingLevel: 2,
                        },
                    },
                    // #12 — `ab-pgrid ps-pgrid-section`. MEASURED heading: 26px w700
                    // ls normal, START aligned — `bold` + `start` again, a third
                    // treatment distinct from both of the above.
                    {
                        id: "category-sections-4",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "standard",
                            categoryId: "$category:3",
                            title: { ar: null, en: null },
                            titleStyle: "bold",
                            titleAlign: "start",
                            showSectionTitle: true,
                            productsPerSection: 8,
                            columnsPerRow: 4,
                            headingLevel: 2,
                        },
                    },
                    // #13 — `editorial-trio`: a centred headline over three tall image
                    // cards, each with a label button on a hover overlay. Mapped to
                    // `stories` / `story-overlay` — same content model (image + label
                    // + link per card, one headline above).
                    {
                        id: "stories-1",
                        type: "stories",
                        visible: true,
                        settings: {
                            variant: "story-overlay",
                            overlayDescription: "hidden",
                            // Their headline is 62 characters; the schema caps titles at
                            // 60, so it is trimmed to the same sentence one clause shorter.
                            title: {
                                ar: "إطلالات الربيع، مع بعض أو كل واحدة لوحدها.",
                                en: "Spring styles to wear together or apart.",
                            },
                            // `stories.eyebrow` is a PLAIN string field, not i18n — unlike
                            // every other section's eyebrow. Passing a LocaleString here
                            // fails the save with `invalid_type`.
                            eyebrow: "FOLLOW US",
                            readAllLabel: { ar: null, en: null },
                            readAllHref: "",
                            headingLevel: 2,
                        },
                        blocks: ["Facebook", "Instagram", "TikTok"].map((label, i) => ({
                            id: `story-${i}`,
                            type: "story",
                            settings: {
                                tag: { ar: null, en: null },
                                title: { ar: label, en: label },
                                description: { ar: null, en: null },
                                image: null,
                                readTime: { ar: null, en: null },
                                href: "",
                            },
                        })),
                    },
                    // #14 — `ps-featured-press`: the inventory's "press logos" MISSING
                    // row, ported as the `test-press` testimonials variant.
                    {
                        id: "testimonials-1",
                        type: "testimonials",
                        visible: true,
                        settings: {
                            variant: "test-press",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اتكلموا عننا", en: "Featured by press" },
                            headingLevel: 2,
                        },
                        blocks: [1, 2, 3].map((n) => ({
                            id: `press-${n}`,
                            type: "testimonial",
                            settings: {
                                quote: { ar: null, en: null },
                                author: { ar: null, en: null },
                                role: { ar: null, en: null },
                                image: null,
                                rating: 5,
                            },
                        })),
                    },
                    // #15 — `ps-rev-carousel`: the review carousel. Mapped to
                    // `test-carousel`, our existing layout.
                    {
                        id: "testimonials-2",
                        type: "testimonials",
                        visible: true,
                        settings: {
                            variant: "test-carousel",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "أكتر من ٢٠٠٠ تقييم سعيد", en: "Over 2,000 happy reviews" },
                            headingLevel: 2,
                        },
                        blocks: [1, 2, 3, 4].map((n) => ({
                            id: `review-${n}`,
                            type: "testimonial",
                            settings: {
                                quote: { ar: null, en: null },
                                author: { ar: null, en: null },
                                role: { ar: null, en: null },
                                image: null,
                                rating: 5,
                            },
                        })),
                    },
                    // #16 — `newsletter-luxe`: a centred 520px column, serif title,
                    // pill field + pill submit. Mapped to `news-center`; the serif face
                    // is their local override and we carry one heading face per store.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-center",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "رسائل من الاستوديو", en: "Notes from the studio" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                            image: null,
                            fineprint: { ar: null, en: null },
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // The `ps` grid footer — the archetype avyro/elite/nature/sharp
                    // share. Nature's first cell is the newsletter rather than a logo,
                    // which `footer-directory` already covers.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-directory", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        id: "impact",
        name: { ar: "تأثير", en: "Impact" },
        description: {
            ar: "إطارات منتجات دائرية مع زر كحلي جريء.",
            en: "Rounded product frames on a bold navy CTA, sentence-case names.",
        },
        tokens: {
            colors: {
                // MEASURED on blast-books (2026-07-20): `:root` carries
                // `--color-primary: 15, 26, 60` and `--buy-btn-bg: #0F1A3C`; the
                // header ink, the featured-banner CTA, the compare title and the whole
                // footer ground all compute to `rgb(15,26,60)`. Already correct.
                primary: "#0f1a3c",
                // MEASURED, replacing the unmeasured `#ff0000`. This is the SEVENTH
                // preset caught carrying a discount signal as its accent band:
                // `#ff0000` is blast's `--product-badge-bg`, the "Sale!" chip fill.
                // Applied as a band it paints the header and every accent section
                // pure red against a white page.
                //
                // Their actual band is the same navy, corroborated four ways on their
                // own page: `--color-primary: 15, 26, 60`, `--buy-btn-bg: #0F1A3C`,
                // the newsletter button `--nl-btn: #0F1A3C`, and the footer computes
                // `rgb(15,26,60)`.
                accent: "#0f1a3c",
                // MEASURED: body ground and header both compute rgb(255,255,255).
                surface: "#ffffff",
                // MEASURED: body ink computes rgb(19,19,22), not the #151515 this
                // carried.
                ink: "#131316",
                // FIX PHASE B — the FOOTER BAND. The audit found 10 of 12 families on
                // the wrong footer hex because every archetype hard-bound the band to
                // a shared role (`accent` / ink / alt). MEASURED per family.
                // MEASURED navy footer; ours grounded on ink #131316.
                footerSurface: "#0f1a3c",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                // MEASURED and confirmed here, unlike hype: blast's `document.body`
                // computes to the platform UI stack, but every authored block on the
                // page — product names, row titles, the hero kicker and button, the
                // featured banner, the compare title — computes to `Jost`. The
                // platform stack is only the unstyled default; Jost is the family's
                // actual face.
                headingEn: "Jost",
                bodyEn: "Jost",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "Newsreader",
            },
            // MEASURED: `ab-pgrid-media` and `ab-plist` card media both carry a 14px
            // radius, and the featured banner is 14px too. Already correct.
            radius: "0.875rem",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "52px",
            pageGutterMobile: "20px",
            // Headings paint the BRAND role, not the body ink. The hex is already
            // correct in `colors` above; only the role binding was wrong.
            headingRole: "primary",
            productCardStyle: "clean",
            productCardRadius: "14px",
        },
        // The EIGHTH preset to carry page structure (2026-07-20), conforming to
        // the `TemplateInstance` model avyro settled and elite/nature/sharp/dark/
        // volta/hype confirmed. Section order, variants and settings are the
        // measured blast-books homepage — see the "blast" subsection of
        // docs/plans/2026-07-19-storefront-section-vocabulary.md.
        //
        // Copy is NEUTRAL PLACEHOLDER and every image field is null, for the
        // reasons avyro recorded.
        //
        // blast is 11 sections and needs FOUR categories — a four-card circle rail
        // plus four product rows drawn from four different categories.
        // `$category:N` DROPS its whole section when unresolvable, so a
        // three-category store would silently lose a row and a rail card.
        template: {
            home: {
                sections: [
                    // MEASURED: `.ab-header` computes `rgb(255,255,255)` with navy ink
                    // (`rgb(15,26,60)`) over the white page ground. Our `header.surface`
                    // defaults to the ACCENT band, and this preset's accent is their
                    // navy, so leaving it unset paints a navy header under a white page.
                    // Same call as avyro, elite, dark, volta and hype.
                    { id: "header", type: "header", locked: true, visible: true, settings: { variant: "grand-centered", surface: "page", mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false } },
                    // Declared and switched OFF, exactly as hype's is, and the
                    // page-level diff is what caught the need for it: a section the
                    // preset does not LIST is merged back in VISIBLE by
                    // `applyThemeToStore`, so leaving `value-strip` out painted an
                    // 84px trust strip between the hero and the first product row that
                    // blast-books does not have. Absent means "default", not "off".
                    { id: "value-strip-1", type: "value-strip", visible: false, settings: {} },
                    // #0 — `ab-categories`, and it leads the page ABOVE the hero. The
                    // first family in this run to open on a category rail rather than a
                    // hero, and the reason a section-level diff would not have caught it:
                    // every section matched, in the wrong order.
                    //
                    // MEASURED: four cards, each a 64px circle with the name set BESIDE
                    // it, in a snap scroller; the section is 110px at every width from
                    // 390 to 1440. `.ab-categories-title` is present and EMPTY, so the
                    // rail carries no heading — the same finding hype recorded, and the
                    // reason `showSectionTitle` exists.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "avatar-row",
                            showSectionTitle: false,
                            title: { ar: null, en: null },
                            eyebrow: { ar: null, en: null },
                            count: 4,
                            headingLevel: 2,
                        },
                    },
                    // #1 — `rnw-hero rnw-hero--center`, the runway engine CENTRED.
                    // MEASURED inline: `--rnw-h-dt: 75svh` (675 from 768 up),
                    // `--rnw-h-mb: 60svh` (540 at 390), `--rnw-overlay: 42`,
                    // `--rnw-t-dt: 60px` / `-mb: 36px`, `--rnw-b-rad: 99px`,
                    // `--rnw-accent: #FFF`.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "runway",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق التشكيلة", en: "Shop collection" },
                            ctaHref: "all_products",
                            // MEASURED: their `rnw-hero__actions` holds exactly ONE
                            // `rnw-btn`. Left unset, `ctaHrefSecondary` defaults to
                            // `collections` and paints a second pill theirs does not have —
                            // the trap hype recorded.
                            ctaHrefSecondary: "none",
                            image: null,
                            textAlign: "center",
                            overlayOpacity: 42,
                        },
                    },
                    // #2 — `ab-plist` "Business Books", 6 cards on a scrolling track with
                    // "View All" opposite a START-aligned 32px/w800 title.
                    blastRow(1, 0, "slider", 6, "bold", "start", true),
                    // #3 — `hs-timer-offer`: a contained rounded card, title on the
                    // leading edge, three white digit chips through the middle, pill CTA
                    // on the trailing edge. MEASURED 104px at 768 and up, 178px stacked
                    // below. Ported here as `countdown` / `count-strip`.
                    //
                    // `targetDate` is deliberately null: a preset cannot ship a real
                    // date without it being wrong the day after the theme is applied.
                    // With no target the section prints its title and CTA and no digits,
                    // which is the honest empty state; the seller sets the date.
                    {
                        id: "countdown-1",
                        type: "countdown",
                        visible: true,
                        settings: {
                            variant: "count-strip",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "عرض لفترة محدودة", en: "Limited-time offer" },
                            subhead: { ar: null, en: null },
                            targetDate: null,
                            expiredMessage: { ar: "العرض خلص", en: "Offer ended" },
                            ctaLabel: { ar: "تسوق دلوقتي", en: "Shop now" },
                            // `countdown.ctaHref` is a `url` field, not the `link` picker the
                            // hero and banner use, so it takes a real path rather than an
                            // `all_products` sentinel. Left EMPTY, matching how elite's
                            // `count-hero` ships: the renderer only draws the button when
                            // both label and href resolve, so the seller points it somewhere
                            // real. Consequence, stated plainly: their strip's "Shop Now!"
                            // pill is not drawn until the seller sets a destination.
                            ctaHref: "",
                            // MEASURED: their `--to-pattern-*` is a decorative photograph at
                            // full strength with NO scrim. A preset ships no imagery, so the
                            // card renders as a flat band and the dimming is set to 0 so a
                            // seller who adds a pattern gets theirs, not a muddied one.
                            image: null,
                            overlayOpacity: 0,
                            // MEASURED `--to-bg: #ffd600` — a saturated panel on a white
                            // page. Mapped to the accent band rather than the hex.
                            cardSurface: "accent",
                        },
                    },
                    // #4 — `ab-pgrid` "Best Sellers", 8 cards in 4 columns under a
                    // CENTRED 28px/w700 title.
                    blastRow(2, 1, "standard", 8, "bold", "center", true),
                    // #5 — `rnw-hero rnw-hero--left` used MID-PAGE while the page's own
                    // hero is the same engine centred. MEASURED inline:
                    // `--rnw-h-dt: 65svh` (585), `--rnw-h-mb: 50svh` (450),
                    // `--rnw-overlay: 83`, `--rnw-b-rad: 5px`. The `banner-runway`
                    // variant volta ported, and the second family to need it.
                    {
                        id: "banner-1",
                        type: "banner",
                        visible: true,
                        settings: {
                            variant: "banner-runway",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق التشكيلة", en: "Shop collection" },
                            ctaHref: "collections",
                            image: null,
                            overlayOpacity: 83,
                            textAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #6 — `ab-plist` "Romance Books", the second carousel, identical to
                    // #2 but on another category.
                    blastRow(3, 2, "slider", 6, "bold", "start", true),
                    // #7 — `hs-search`: "Didn't find the product you were looking for?"
                    // over a button that hands focus to the header search field.
                    // MEASURED 188px at 768 and up, 146px below. A NEW section type —
                    // the only block in the vocabulary whose call to action is an
                    // ACTION rather than a destination.
                    {
                        id: "search-prompt-1",
                        type: "search-prompt",
                        visible: true,
                        settings: {
                            title: {
                                ar: "مش لاقي المنتج اللي بتدور عليه؟",
                                en: "Didn't find the product you were looking for?",
                            },
                            buttonLabel: { ar: "ابحث", en: "Search" },
                            headingLevel: 2,
                            // MEASURED `--hs-search-bg: #fff4b9` — a pale panel, not a
                            // saturated one, so this takes the SUBTLE band where the timer
                            // strip above takes accent. Two cards, two bands, and only
                            // measuring tells you which.
                            cardSurface: "alt",
                            image: null,
                            patternOpacity: 65,
                        },
                    },
                    // #8 — `ab-comparison-section ab-comparison--square`. The class name
                    // says square and the BOX says otherwise, for the FOURTH family:
                    // MEASURED media 1000x563 = 1.778, i.e. 16:9. Mapped to `wide`,
                    // exactly as dark's, volta's and hype's were.
                    // `--comp-max-width: 1000px`, title 35.2px/w600 in their navy.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "panel",
                            aspect: "wide",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "شوف الفرق", en: "See the transformation" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    // #9 — `ab-featured-products`: a 384x640 banner beside a 3-up grid of
                    // 6 cards at 1024 and up, stacking to a 320/280px banner over a 2-up
                    // grid below. `hero-subtitle`, which `BuyerCategorySection` documents
                    // as the port of "the EasyOrders `ab-featured-hero` (blast, pearl,
                    // peral, phoenix)" — this family's own block, named in the renderer.
                    //
                    // It prints NO heading above itself: the category name is set INSIDE
                    // the banner (32px/w800 white) with "Featured Collection" under it
                    // and "View All" as a navy pill. Drawing both would double it — the
                    // trap volta's `banner-grid` row recorded.
                    blastRow(4, 3, "hero-subtitle", 6, "bold", "start", false),
                    // #10 — `newsletter-luxe`, which nature already mapped to the centred
                    // variant. MEASURED authored `--nl-bg: #fff4b9` with `--nl-text:
                    // #121212` — a PALE band, where hype's identical section is a
                    // near-black `#151F28` and volta's is the accent. Same section,
                    // three different grounds across three families, and only measuring
                    // tells you which: this one takes `alt`.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-center",
                            surface: "alt",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اشترك في نشرتنا", en: "Subscribe to our newsletter" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // MEASURED `ab-footer ab-footer--kids`: a Shop column, a Pages
                    // column, a brand/contact block and APP-STORE BADGES. The footer
                    // survey assigns blast `footer-social` by name — "socials lead,
                    // app-store badges, link row in the bottom bar" — and the live
                    // markup is that archetype.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-social", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        id: "warm",
        name: { ar: "دافئ", en: "Warm" },
        description: {
            ar: "زر ذهبي مع أسماء منتجات بحروف كبيرة ومتباعدة.",
            en: "Gold CTA with wide-tracked uppercase product names.",
        },
        tokens: {
            colors: {
                // MEASURED on elite-furniture (2026-07-20). `primary` is their
                // `--runway-accent` / hero CTA gold, unchanged.
                primary: "#c9a962",
                // WAS `#e32c2b`, the same unmeasured red the avyro note flagged across
                // the other ten `eo-*` presets. `accent` is a BAND colour in our token
                // model — it grounds the header and every accent band. Elite's actual
                // band is the footer ground `--ft-bg: #7E451D`, corroborated three
                // independent ways: the footer computes to rgb(126,69,29), the
                // editorial feature is authored `--ed-accent: #7E451D`, and the
                // newsletter button is `--ni-btn-bg: #7E451D`. The red appears nowhere
                // on their page.
                accent: "#7e451d",
                surface: "#ffffff",
                // Their section headings compute to rgb(28,28,28); body ink is
                // rgb(19,19,22). Kept at the heading value — the 3-unit difference is
                // imperceptible and this is the colour the page's type actually reads
                // as at every size above 16px.
                ink: "#1c1c1c",
                // FIX PHASE A — the PAGE GROUND, distinct from `surface`. Their
                // content_container paints this and every section is transparent over
                // it, so cards/photos read as raised panels. Only 5 of 12 families
                // have a non-white ground; the other 7 deliberately omit this token.
                pageGround: "#EFEFEF",
                // FIX PHASE B — the FOOTER BAND. The audit found 10 of 12 families on
                // the wrong footer hex because every archetype hard-bound the band to
                // a shared role (`accent` / ink / alt). MEASURED per family.
                // Band already correct; their ink is the CREAM #FEE6CE, not white.
                footerSurface: "#7e451d",
                footerInk: "#FEE6CE",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                headingEn: "Instrument Sans",
                bodyEn: "Instrument Sans",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "System Serif",
            },
            radius: "0",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "32px",
            pageGutterMobile: "18px",
            // `card` is CORRECT here and was already right: `CardWithButton` is
            // documented in its own header as "a faithful port of the EasyOrders
            // 'Elite Supplements' product card (`ps-pgrid-card`)" — THIS family's
            // card. Measured on elite-furniture: 320×408 tile, 320×320 (1:1) media,
            // transparent ground, radius 0. Unlike avyro (which needed `bordered`),
            // no change was required.
            productCardStyle: "card",
            productCardRadius: "0",
        },
        // The SECOND preset to carry page structure (2026-07-20, family 2 of 13).
        // Section order, variants and settings are the measured elite-furniture
        // homepage — see the "elite" subsection of
        // docs/plans/2026-07-19-storefront-section-vocabulary.md.
        //
        // Conforms to the model `eo-avyro` settled: same `TemplateInstance` shape,
        // same `$category:N` / `$products:N` slots, same rules (no imagery, copy is
        // neutral placeholder because text is a CONTENT field the seller's own copy
        // is merged over on apply).
        template: {
            home: {
                sections: [
                    // MEASURED: elite's header is rgba(255,255,255,.96) over the page
                    // ground, same as avyro. Our header field defaults to the ACCENT
                    // band, and this preset's accent is their brown — leaving it unset
                    // would paint a brown header under a white page.
                    {
                        id: "header",
                        type: "header",
                        locked: true,
                        visible: true,
                        settings: { variant: "luxe-centered", surface: "page", transparentOverHero: true, mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false },
                    },
                    // #0 — `runway-hero runway-hero--center`: dark editorial frame,
                    // kicker + serif title + lede + one pill CTA over a scrim.
                    // `--runway-min-h: 72vh` (= the measured 648px at a 900px viewport)
                    // and `--runway-overlay: 0.25` are authored inline on their element.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "runway",
                            showEyebrow: true,
                            eyebrow: { ar: "الموسم الجديد", en: "New season" },
                            headline: { ar: "إطلالة الموسم", en: "The season edit" },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق الآن", en: "Shop now" },
                            // The hero's `ctaHref` is an ENUM, not a free URL
                            // (all_products | collections | scroll_below). Theirs points at a
                            // specific category, which a preset cannot name for an arbitrary
                            // store; `collections` is the nearest expressible destination.
                            ctaHref: "collections",
                            // Their runway hero ships ONE button. Our secondary CTA had no
                            // off switch before this family (it defaults to `collections`
                            // and renders unconditionally), so `"none"` was added to the
                            // enum — see `sections/hero.ts`.
                            ctaHrefSecondary: "none",
                            mediaSource: "upload",
                            image: null,
                            // Their `--runway-overlay: 0.25`, as a 0–100 range.
                            overlayOpacity: 25,
                            // `runway-hero--center`, not the family default start.
                            textAlign: "center",
                            headingLevel: 1,
                        },
                    },
                    // Elite ships NO trust strip. Omitting the section is not enough:
                    // `BuyerStoreHomepage` pins a SYNTHETIC value strip after the hero
                    // whenever the list contains no `value-strip` at all. The shim tests
                    // for the TYPE, not visibility — see the avyro preset for the full
                    // note.
                    {
                        id: "value-strip-1",
                        type: "value-strip",
                        visible: false,
                        settings: {},
                    },
                    // #1 — `ab-categories-section`: the bento mosaic (measured 5 tiles,
                    // 664/556/556/664/556 wide) under a kicker. Identical to avyro's,
                    // which is expected: the two share the `ab-` categories engine.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "bento",
                            eyebrow: { ar: "أفضل المجموعات", en: "Best seller collections" },
                            title: { ar: "تسوق حسب الفئة", en: "Shop by category" },
                            headingLevel: 2,
                        },
                    },
                    // #2 / #4 / #6 / #8 — four product rows in THREE treatments, in a
                    // DIFFERENT order from avyro's: elite runs banner-grid → slider →
                    // banner-grid → standard where avyro runs slider → banner-grid →
                    // slider → standard. Measured per row, not assumed from the sibling
                    // family.
                    categoryRow(1, 0, { variant: "banner-grid", columnsPerRow: 4 }),
                    // #3 — `psc-compare`, the compare panel. `--aspect-squareish` (4:3)
                    // here where avyro uses ultrawide; measured shell 1376×1122 at 1440.
                    // `--psc-split: 50` is authored inline.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "panel",
                            aspect: "squareish",
                            eyebrow: { ar: "المجموعة", en: "The collection" },
                            title: { ar: "اختر لونك", en: "Choose your colour" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    categoryRow(2, 1, { variant: "slider", columnsPerRow: 4 }),
                    // #5 — `promo-countdown-hero`: the section elite needs that no other
                    // family has yet. Schema existed; the RENDERER did not (see the plan
                    // doc's half-landed-ports table) and was ported for this family.
                    // `--pch-min-h: 52vh` = `comfortable`; `--pch-overlay: 0.48` = 48.
                    {
                        id: "countdown-1",
                        type: "countdown",
                        visible: true,
                        settings: {
                            variant: "count-hero",
                            eyebrow: { ar: "لفترة محدودة", en: "Limited time only" },
                            title: { ar: "عرض خاص", en: "Seasonal offer" },
                            subhead: { ar: null, en: null },
                            // No date: a preset cannot know the seller's sale window, and
                            // shipping a stale one would render an expired timer on day one.
                            // The section still paints its copy + CTA until the seller picks.
                            expiredMessage: { ar: "إحنا أونلاين دلوقتي!", en: "We're live!" },
                            image: null,
                            overlayOpacity: 48,
                            height: "comfortable",
                            ctaLabel: { ar: "تسوق العرض", en: "Shop the sale" },
                            ctaHref: "",
                        },
                    },
                    categoryRow(3, 2, { variant: "banner-grid", columnsPerRow: 4 }),
                    // #7 — `editorial-feature editorial-feature--img-left`. Elite carries
                    // a kicker here where avyro does not; the eyebrow's presence is part
                    // of the layout, so it ships set rather than null.
                    {
                        id: "about-1",
                        type: "about",
                        visible: true,
                        settings: {
                            variant: "about-editorial",
                            eyebrow: { ar: "من داخل الورشة", en: "Inside the atelier" },
                            title: { ar: "قصتنا", en: "Our story" },
                            body: { ar: null, en: null },
                            image: null,
                            imagePosition: "start",
                            ctaLabel: { ar: "اعرف أكتر", en: "Read more" },
                            ctaHref: "",
                            headingLevel: 2,
                        },
                    },
                    categoryRow(4, 3, { variant: "standard", columnsPerRow: 4 }),
                    // #9 — `newsletter-immersive--h-comfortable`, `--ni-overlay-a: 0.45`.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-immersive",
                            eyebrow: { ar: "تابع جديدنا", en: "Keep me updated" },
                            title: { ar: "النشرة البريدية", en: "Newsletter" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                            height: "comfortable",
                            overlayOpacity: 45,
                            image: null,
                            fineprint: { ar: null, en: null },
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // `ps-footer-*` — the same grid footer archetype avyro/nature/sharp
                    // ship. Confirmed by class prefix on elite-furniture's own footer.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-directory", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        id: "display",
        name: { ar: "عرض", en: "Display" },
        description: {
            ar: "برتقالي عالي التباين مع أسماء منتجات في المنتصف.",
            en: "High-contrast orange accent with centred product names.",
        },
        tokens: {
            colors: {
                // MEASURED on hype-books (2026-07-20): their `:root` carries
                // `--color-primary: 10, 25, 61` and `--buy-btn-bg: #0A193D`, and every
                // product name computes to rgb(10,25,61). Deep navy.
                primary: "#0a193d",
                // MEASURED, replacing the unmeasured `#f54e05`. This is the SIXTH
                // preset caught carrying a discount-badge colour as its accent band:
                // `#f54e05` is hype's `ab-sale-badge` fill and its sale-price ink — a
                // price signal, not a surface. Applied as the band it paints the
                // header and every accent section bright orange against a white page.
                //
                // Their actual band is the same navy, corroborated three ways on their
                // own page: the marquee is authored `--mq-bg: #0A193D`, the footer
                // computes `rgb(15,26,60)`, and the buy button is `#0A193D`.
                accent: "#0a193d",
                // MEASURED: body ground and header are both rgb(255,255,255).
                surface: "#ffffff",
                // MEASURED: body ink computes to rgb(19,19,22). Already correct.
                ink: "#131316",
                // FIX PHASE B — the FOOTER BAND. The audit found 10 of 12 families on
                // the wrong footer hex because every archetype hard-bound the band to
                // a shared role (`accent` / ink / alt). MEASURED per family.
                // MEASURED footer rgb(15,26,60); ours grounded on ink #131316.
                footerSurface: "#0f1a3c",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                // NOT MEASURED-CONFIRMED, and said plainly: their page loads NO web
                // font — `document.body` computes to the platform UI stack
                // (`-apple-system, system-ui, Segoe UI, Roboto…`). We have no
                // "system" typography option, so the existing Jost is left in place
                // as the nearest geometric sans rather than being changed to another
                // guess. Recorded as a difference.
                headingEn: "Jost",
                bodyEn: "Jost",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "Newsreader",
            },
            radius: "0",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "24px",
            pageGutterMobile: "16px",
            productCardStyle: "centered-accent",
            // MEASURED: `ab-pgrid-card` / `ab-plist-card` media carries no radius —
            // 0px on every card in all four rows. Already correct.
            productCardRadius: "0",
        },
        // The SEVENTH preset to carry page structure (2026-07-20), conforming to
        // the `TemplateInstance` model avyro settled and elite/nature/sharp/dark/
        // volta confirmed. Section order, variants and settings are the measured
        // hype-books homepage — see the "hype" subsection of
        // docs/plans/2026-07-19-storefront-section-vocabulary.md.
        //
        // Copy is NEUTRAL PLACEHOLDER and every image field is null, for the
        // reasons avyro recorded.
        //
        // hype is 13 sections and needs FOUR categories — a four-tile caption rail
        // plus four product rows drawn from four different categories.
        // `$category:N` DROPS its whole section when unresolvable, so a
        // three-category store would silently lose a row and a tile.
        template: {
            home: {
                sections: [
                    // MEASURED: `.ab-header` computes `rgb(255,255,255)` with navy ink
                    // over the white page ground. Our `header.surface` defaults to the
                    // ACCENT band, and this preset's accent is their navy, so leaving it
                    // unset paints a navy header under a white page. Same call as
                    // avyro, elite, dark and volta.
                    { id: "header", type: "header", locked: true, visible: true, settings: { variant: "aisle", surface: "page", mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false } },
                    // #0 — `rnw-hero rnw-hero--center`, the runway engine CENTRED.
                    // MEASURED inline: `--rnw-h-dt: 70svh` (= 630px at 768 and up),
                    // `--rnw-h-mb: 65svh` (= 585 at 390), `--rnw-overlay: 32`,
                    // `--rnw-t-dt: 60px` / `-mb: 36px`, `--rnw-b-rad: 5px`.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "runway",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق التشكيلة", en: "Shop collection" },
                            ctaHref: "all_products",
                            // MEASURED: their `rnw-hero__actions` holds exactly ONE `rnw-btn`.
                            // `ctaHrefSecondary` defaults to `collections`, so leaving it
                            // unset drew a second "Browse" pill theirs does not have — the
                            // same trap the elite family recorded when it added the `none`
                            // option.
                            ctaHrefSecondary: "none",
                            image: null,
                            textAlign: "center",
                            overlayOpacity: 32,
                        },
                    },
                    // hype ships NO trust strip. Present-and-hidden rather than omitted,
                    // because `BuyerStoreHomepage` pins a SYNTHETIC value strip after
                    // the hero whenever the list contains no `value-strip` TYPE at all.
                    { id: "value-strip-1", type: "value-strip", visible: false, settings: {} },
                    // #1 and #3 — TWO `ab-marquee-section` bands, one above the category
                    // rail and one below it. Ported for dark; hype needed no new stop.
                    //
                    // MEASURED inline, and it CORROBORATES the `medium` ladder rung on a
                    // second family including its mobile values, which dark could not:
                    // `--mq-font-size-dt: 48px` / `-mb: 24px`, `--mq-lh-dt: 68px` /
                    // `-mb: 48px` — exactly `medium: [48, 24, 68, 48]`. Also
                    // `--mq-weight: 700`, `--mq-letter-spacing: 2px`, `--mq-tilt: 0deg`,
                    // and `--mq-bg: #0A193D` / `--mq-color: #ffffff`, i.e. the ACCENT
                    // band (dark's is near-black, volta's is the page ground — three
                    // families, three surfaces, and only measuring tells you which).
                    //
                    // Their track is bare `ab-marquee-item` spans with no separator
                    // element, so `separator: "none"`.
                    hypeMarquee(1),
                    // #2 — `ab-categories ab-figma-categories`: FOUR square 328x328
                    // photo tiles in a scrollable track, each with a small "Shop"
                    // kicker (14px/w600) over an uppercase 20px/w700 label, both white,
                    // set over the artwork with NO scrim.
                    //
                    // `caption-tile`, which `BuyerCollectionGrid` documents as the port
                    // of "the EasyOrders `ab-figma-categories` (hype)" — this family's
                    // own row, named in the renderer. NOT `scrim-tile` (dark/volta),
                    // which dims the photo and centres one large label.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "caption-tile",
                            // MEASURED: every tile carries a 14px/w600 kicker reading
                            // "Shop" above its uppercase label. `caption-tile` prints the
                            // section `eyebrow` on each tile, which is exactly this slot.
                            eyebrow: { ar: "تسوق", en: "Shop" },
                            // MEASURED: their `.ab-categories-title` is present but EMPTY —
                            // the rail carries no heading at all, and the tiles' own labels
                            // are the whole of its copy. `showSectionTitle` was ADDED for
                            // this: an empty `title` alone falls back to the built-in
                            // "Shop by Collection", so the heading could not be turned off.
                            showSectionTitle: false,
                            title: { ar: null, en: null },
                            count: 4,
                            headingLevel: 2,
                        },
                    },
                    hypeMarquee(2),
                    // #4 — `ab-pgrid` "Best Sellers": 10 cards, 4 columns, title CENTRED.
                    //
                    // Their row is not category-scoped; ours is, because
                    // `category-sections` takes a `categoryId`. Bound to the Fantasy
                    // slot so all four categories appear exactly once across the page.
                    // Stated plainly: the SET of products differs from theirs, the
                    // layout does not.
                    hypeRow(1, 2, "standard", 10, "center"),
                    // #5 — `ab-comparison-section`. Their modifier is `--square` and it
                    // is a lie for the THIRD family running: the media MEASURES
                    // 1000x563 = 1.778, i.e. 16:9. Mapped from the BOX, so `wide`.
                    // `--comp-max-width: 1000px`, title 32px/w600 centred, labels
                    // 28px/w600.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "panel",
                            aspect: "wide",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "شوف الفرق", en: "See the transformation" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    // #6 — the first `ab-plist`: 6 cards on a 2026px track, heading left
                    // with a "View All" opposite.
                    hypeRow(2, 1, "slider", 6, "start"),
                    // #7 — `rnw-hero rnw-hero--left`, MID-PAGE and below the first
                    // product row, while the page-leading hero is the SAME engine
                    // centred. `hero.maxPerTemplate` is 1, so this ships as the
                    // `banner-runway` variant volta ported.
                    //
                    // MEASURED inline: `--rnw-h-dt: 55svh` (= 495px from 768 up),
                    // `--rnw-h-mb: 37svh` (= 333 at 390), `--rnw-overlay: 87` — nearly
                    // opaque, which is why their copy reads over a busy photograph —
                    // left-aligned, `--rnw-b-rad: 99px` (a pill).
                    {
                        id: "banner-1",
                        type: "banner",
                        visible: true,
                        settings: {
                            variant: "banner-runway",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق التشكيلة", en: "Shop collection" },
                            // `banner.ctaHref` is an ENUM, not a URL.
                            ctaHref: "collections",
                            image: null,
                            overlayOpacity: 87,
                            textAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #8 — the second `ab-plist`.
                    hypeRow(3, 3, "slider", 6, "start"),
                    // #9 — `custom-static-promo`, PORTED FOR THIS FAMILY as the
                    // `banner-promo` variant. A contained 16px-radius panel with the
                    // copy centred over the artwork and a pill CTA under it.
                    //
                    // MEASURED 332px at 1440 / 296 at 768 / 280 at 390, the height
                    // driven entirely by percentage padding. `--promo-btn-bg: #151F28`,
                    // `--promo-h-size: 40px`, `--promo-d-size: 14px`, and NO scrim at
                    // all — hence `overlayOpacity: 0`.
                    {
                        id: "banner-2",
                        type: "banner",
                        visible: true,
                        settings: {
                            variant: "banner-promo",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق دلوقتي", en: "Shop now" },
                            ctaHref: "all_products",
                            image: null,
                            overlayOpacity: 0,
                            textAlign: "center",
                            headingLevel: 2,
                        },
                    },
                    // #10 — `ab-featured-products--rows`: a 1392x412 banner with the
                    // section title set over it, then 6 LANDSCAPE row-cards in two
                    // columns (689x266 each, a 180x240 thumbnail beside the copy and two
                    // stacked action buttons). Our `rows` variant, whose frame was
                    // ported from this very engine — its comment records the 689px card
                    // and the 35px/24px padding this page authors.
                    hypeRow(4, 0, "rows", 6, "center"),
                    // #11 — `rp-reviews`, PORTED FOR THIS FAMILY as the `test-reviews`
                    // variant. Six review cards in a 3-column 1200px grid, each a 4:3
                    // photo over name + "Verified Buyer" + five stars + the review text,
                    // with a footer strip naming the product.
                    {
                        id: "testimonials-1",
                        type: "testimonials",
                        visible: true,
                        settings: {
                            variant: "test-reviews",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "آراء عملائنا", en: "What our readers say" },
                            description: { ar: null, en: null },
                            headingLevel: 2,
                        },
                        // Six blocks, matching their six cards. Copy is placeholder; the
                        // image and product slots are empty for the same reason every
                        // other preset's are.
                        blocks: [1, 2, 3, 4, 5, 6].map((n) => ({
                            id: `testimonial-1-${n}`,
                            type: "testimonial",
                            settings: {
                                quote: { ar: null, en: null },
                                author: { ar: null, en: null },
                                role: { ar: "مشتري موثّق", en: "Verified Buyer" },
                                image: null,
                                rating: 5,
                                productId: [],
                            },
                        })),
                    },
                    // #12 — `newsletter-luxe`, which nature mapped to the centred
                    // layout. Authored inline `background: #151F28; color: #FFF` with a
                    // gold `--nl-btn: #C9A962` submit — a DARK band, not the navy accent
                    // one, so `surface: "ink"` rather than `"accent"`.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-center",
                            surface: "ink",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اشترك في نشرتنا", en: "Subscribe to our newsletter" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // Their footer is a plain `ab-footer` with `ab-footer-brand-col` /
                    // `ab-footer-nav-col` / `ab-footer-newsletter-col` — the
                    // contact-brand archetype the footer survey already assigns to hype
                    // by name. NOT `footer-wave` (prime) and NOT `footer-brand`
                    // (dark/volta).
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-contact", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        id: "polished",
        name: { ar: "مصقول", en: "Polished" },
        description: {
            ar: "تحريري وحاد — أسماء صغيرة بحروف كبيرة وزر ذهبي.",
            en: "Editorial and sharp — small uppercase names, gold CTA.",
        },
        tokens: {
            colors: {
                // MEASURED on pearl-bags (2026-07-20) and corroborated FOUR ways on
                // their own page: `--stl-accent`, `--runway-accent` and `--tp-accent`
                // are all `#C9A962`, and the runway hero's primary button is filled
                // with it. Already correct.
                primary: "#c9a962",
                // MEASURED, replacing the unmeasured `#cd1515`. This is the NINTH
                // preset caught carrying a discount signal as its accent band.
                // `#cd1515` IS on pearl's page — it is `--product-badge-bg`, the sale
                // chip, and the discounted price colour in `EditorialCard`. But
                // `accent` is a BAND in our token model: it grounds the header and
                // every accent section. Applied as a band it paints a pillarbox-red
                // header over a white page, which is nothing pearl does.
                //
                // The gold is the page's one authored accent, so accent == primary
                // here — the same resolution `eo-prime` reached with its navy. It also
                // lands correctly: `feat-promise`'s medallion tints from the accent,
                // and their `--tp-accent` is exactly this gold.
                accent: "#c9a962",
                // MEASURED: body ground computes rgb(255,255,255). Already correct.
                surface: "#ffffff",
                // MEASURED: body ink computes rgb(19,19,22), not the #212a2f this
                // carried. `#212a2f` is real but narrower — it is the product NAME
                // colour inside `EditorialCard`, which pins it itself. Same correction
                // blast and prime took.
                ink: "#131316",
                // FIX PHASE B — the FOOTER BAND. The audit found 10 of 12 families on
                // the wrong footer hex because every archetype hard-bound the band to
                // a shared role (`accent` / ink / alt). MEASURED per family.
                // Their footer is BLACK; ours rendered the light `alt` band #F1F1F1.
                footerSurface: "#000000",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                // MEASURED and unusually well-evidenced for this workstream: `Jost`
                // computes directly on `.ab-plist-card`, `.ab-plist-name`,
                // `.ab-plist-price`, `.ab-marquee-item` and the sale badge. (The
                // blast caveat still applies to `document.body`, which computes the
                // platform UI stack because nothing styles it.) Cairo for Arabic,
                // consistent with every other family. Unchanged.
                headingEn: "Jost",
                bodyEn: "Jost",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "System Serif",
            },
            // MEASURED: `.ab-plist-card` and `.ab-plist-media` both compute
            // `border-radius: 0px`, and so does the compare frame. The 999px pills on
            // the runway and compare CTAs are component decisions those variants pin
            // themselves, not a store radius. Already correct.
            radius: "0",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "24px",
            pageGutterMobile: "16px",
            // VERIFIED against the renderer rather than assumed — the mistake avyro's
            // assembly caught. `EditorialCard`'s own header names it "a faithful port
            // of the EasyOrders 'Pearl Watches' product card (`ab-pgrid-card`)", and
            // every value in it matches what was measured here: media aspect 1/1.2,
            // name 12px w400 UPPERCASE #212a2f, sale price #cd1515, radius 0.
            productCardStyle: "editorial",
            productCardRadius: "0",
        },
        // The TENTH preset to carry page structure (2026-07-20), conforming to the
        // `TemplateInstance` model avyro settled. Section order, variants and
        // settings are the measured pearl-bags homepage — see the "pearl"
        // subsection of docs/plans/2026-07-19-storefront-section-vocabulary.md.
        //
        // Copy is NEUTRAL PLACEHOLDER and every image field is null, for the
        // reasons avyro recorded.
        //
        // pearl is 13 sections and needs FIVE categories: a four-tile portrait rail
        // plus four product rows, with the featured row's banner pointing at a
        // fifth. `$category:N` DROPS its whole section when unresolvable, so a
        // store with fewer categories silently loses rows.
        //
        // pearl needed NO new section TYPE and exactly ONE new variant
        // (`features` / `feat-promise`) — the narrowest port list of any family so
        // far, and the strongest sign yet that the type vocabulary is closing.
        template: {
            home: {
                sections: [
                    // MEASURED: `.ab-header` computes `rgba(0,0,0,0)` with WHITE ink — it
                    // floats transparently over the opening image slider. Our `surface`
                    // has no transparent slot, so `ink` is the faithful choice: it keeps
                    // the light-type-on-dark-ground reading their header has, and it
                    // matches the footer, which genuinely computes pure black. `accent`
                    // (the default) would paint it gold with dark ink.
                    { id: "header", type: "header", locked: true, visible: true, settings: { variant: "luxe-centered", surface: "ink", transparentOverHero: true, mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false } },
                    // Declared and switched OFF. pearl's only trust block is the
                    // `trust-promise` bar much further down, which is a `features`
                    // section, not a `value-strip`. A section the preset does not LIST is
                    // merged back in VISIBLE by `applyThemeToStore`, and
                    // `BuyerStoreHomepage` additionally pins a SYNTHETIC value strip
                    // after the hero whenever the list contains no `value-strip` TYPE at
                    // all — so omitting it paints a strip pearl does not have. Absent
                    // means "default", not "off".
                    { id: "value-strip-1", type: "value-strip", visible: false, settings: {} },
                    // #0 — `ab-slider`: FOUR full-bleed artwork slides over a progress
                    // bar, a pause button and prev/next arrows. `edition`, not a variant
                    // of its own — the census note settled this: their slides carry
                    // `hasText: false` because all copy is baked into the uploaded
                    // artwork, and `edition` already renders the whole slide as the link
                    // when a slide has a destination but no copy.
                    //
                    // MEASURED: 1392x609 at 1440 (ratio 2.286) and 390x607 at 390
                    // (ratio 0.643) — the frame flips to portrait below their 769
                    // breakpoint. `data-autoplay="5000"`.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "edition",
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            // SECONDS, not the milliseconds `data-autoplay` carries. The
                            // schema range is 2-10 and the validator rejects anything else —
                            // the check avyro's assembly added after this shipped wrong once.
                            autoplayInterval: 5,
                            textAlign: "start",
                            // MEASURED: their slides carry NO scrim. The artwork is the whole
                            // slide and an overlay would grey it.
                            overlayOpacity: 0,
                            // pearl is `ab-slider` upstream, in its arrows configuration —
                            // NOT the `edition-hero-slider` rail this variant defaults to.
                            // Verified on pearl-bags (pearl-watches is a different engine
                            // entirely and is not this family's source).
                            sliderControls: "inline-arrows",
                        },
                        blocks: [1, 2, 3, 4].map((n) => ({
                            id: `hero-slide-${n}`,
                            type: "slide",
                            settings: {
                                image: null,
                                headline: { ar: null, en: null },
                                subhead: { ar: null, en: null },
                                ctaLabel: { ar: null, en: null },
                                ctaHref: "",
                            },
                        })),
                    },
                    // #1 — `ab-marquee-section`, the same band dark ported. MEASURED
                    // inline: `--mq-font-size-dt: 48px` / `-mb: 24px`,
                    // `--mq-lh-dt: 69px` / `-mb: 48px`, `--mq-weight: 900`,
                    // `--mq-letter-spacing: 3px`, `--mq-speed-dt: 15s` / `-mb: 12s`,
                    // `--mq-tilt: -5deg`, `--mq-bg: #E70022`, `--mq-color: #ffffff`.
                    //
                    // `size: medium` is an EXACT match — our `medium` stop is
                    // [48, 24, 68, 48] against their 48/24/69/48. That stop was
                    // INTERPOLATED from dark's single authored pair; pearl is the second
                    // family to author it and corroborates it to within 1px of line
                    // height, so `medium` is now measured rather than interpolated.
                    {
                        id: "marquee-band-1",
                        type: "marquee-band",
                        visible: true,
                        settings: {
                            // Their band is `#E70022` red with white 900-weight type. Red is
                            // NOT this palette's accent (the gold is, four ways), and a
                            // section-local band colour is not in the token model — the same
                            // call prime recorded for its authored blue marquee. `ink` is
                            // chosen over `accent` because it preserves the relationship that
                            // actually reads: light type on a dark ground. Departure recorded.
                            surface: "ink",
                            size: "medium",
                            // Their 15s desktop sweep is FASTER than our fastest stop (22s).
                            // `fast` is the closest available; departure recorded.
                            speed: "fast",
                            direction: "start",
                            // `-5deg`. Our `up` is `-2deg` — same direction, shallower.
                            tilt: "up",
                            // A DOT. Their innerText reads as four phrases running together,
                            // which is why the first pass set "none" — but the separator is
                            // drawn by a pseudo-element, so it never appears in text content.
                            // The screenshot shows it plainly between phrases. Corrected by
                            // LOOKING, the third defect on this page that measuring missed.
                            separator: "dot",
                            textCase: "upper",
                        },
                        blocks: [1, 2].map((n) => ({
                            id: `marquee-line-1-${n}`,
                            type: "marquee-line",
                            settings: { text: { ar: null, en: null } },
                        })),
                    },
                    // #2 — `ab-featured-products`: a leading banner cell linking to a
                    // category, then product cards, all in ONE horizontally scrolling
                    // 500px track (measured `scrollWidth` 2479 against a 1392 client, so
                    // it genuinely scrolls; `overflow-x: auto`, `flex-wrap: nowrap`).
                    //
                    // `hero-split`. This mapping was settled by ELIMINATION across three
                    // candidates, MEASURING each rather than reading the class name —
                    // the "check the existing variants before building" discipline, and
                    // the harder case where the answer is a GEOMETRY-first compromise:
                    //
                    //  - `hero-subtitle` (blast): a poster banner over a GRID. Right
                    //    banner look, but the grid stacks banner-over-cards on mobile and
                    //    ran +142% at 390 / +402% at 768. Navy PILL CTA, not pearl's link.
                    //  - `hero-eyebrow` (dark/volta): an overlay poster + scroller with a
                    //    text CTA — the right banner look and CTA — BUT its wrapper falls
                    //    under the blanket `flex-direction:column` at <=768, so it too
                    //    stacks the banner tall: MEASURED +142% at 390 / +350% at 768.
                    //  - `hero-split` (prime): a split card with the copy BELOW the image
                    //    and a red CTA — the wrong banner LOOK, but the ONLY featured
                    //    variant with the mobile-scroller exemption, so it stays a
                    //    side-by-side single-row scroller at EVERY width, which is
                    //    exactly pearl's own behaviour (MEASURED: banner 250 + cards 233,
                    //    one 369px row at 390).
                    //
                    // Pearl's featured is an overlay poster beside a horizontal scroller
                    // at all widths. NO existing variant is that exact combination, so
                    // the choice is between the right BANNER (hero-eyebrow, +350% mobile)
                    // and the right GEOMETRY (hero-split, +4%/+64%/+4%). Geometry wins —
                    // it is what the page-level diff protects, a section 4.5x too tall on
                    // tablet reads as broken, and hero-split's red CTA is at least in the
                    // family of pearl's OWN reds (`#cd1515` badge/sale is on every card).
                    // The two banner departures — a split card rather than an overlay
                    // poster, and a red pill rather than a plain white link — are
                    // recorded in the honest-differences list, not hidden.
                    {
                        id: "category-section-1",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "hero-split",
                            categoryId: "$category:0",
                            title: { ar: null, en: null },
                            showSectionTitle: true,
                            showViewAll: true,
                            showBanner: true,
                            bannerImage: null,
                            bannerEyebrow: { ar: null, en: null },
                            // Their single line "New Arrivals" — the category name — is the
                            // banner's copy. `hero-split` prints `bannerTitle` below the
                            // image.
                            bannerTitle: { ar: "وصل حديثًا", en: "New Arrivals" },
                            bannerSubtitle: { ar: null, en: null },
                            bannerCtaLabel: { ar: "عرض الكل", en: "View All" },
                            productsPerSection: 6,
                            titleStyle: "plain",
                            titleAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #3 — `ab-categories`: four 4:5 portrait tiles with the name set
                    // BELOW the image in extra-light type, in a five-column grid.
                    //
                    // `portrait` — and `BuyerCollectionGrid` documents that variant as a
                    // port of "the EasyOrders `ab-cat-card` portrait row (luxury, pearl,
                    // peral)", i.e. of THIS family's own row. pearl is the first family
                    // to actually use it, which is what surfaced the two defects its
                    // ladder carried; both are fixed with this assembly and recorded in
                    // the renderer.
                    //
                    // MEASURED: heading 48px w200 ls .5px centred; track
                    // `repeat(5, 1fr)` gap 8 at >=769 and `repeat(2, 1fr)` below; cards
                    // aspect-ratio 4/5 at every width; label 12px below 1200, 18px above.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "portrait",
                            showSectionTitle: true,
                            title: { ar: "تسوق حسب المجموعة", en: "Shop by collection" },
                            eyebrow: { ar: null, en: null },
                            // NO `count`, deliberately, for the reason prime recorded: the
                            // field's own `showIf` scopes it to the six grid variants and
                            // `BuyerCollectionGrid` hands `portrait` the FULL category list.
                            // The rail shows whatever the store has.
                            headingLevel: 2,
                        },
                    },
                    // #4 / #6 / #8 — three `ab-plist` carousels, 572px each at 1440,
                    // start-aligned title with "View All" opposite, six cards visible.
                    pearlRow(2, 1),
                    // #5 — `shop-the-look--cream`, already ported for avyro. Four linked
                    // product cards beside a 649x540 hero visual. MEASURED: the section
                    // is 799px at 1440 and stacks below 992 (1239px at 900), and its
                    // ground is `rgb(243,239,232)` — the page's warm off-white, so `alt`.
                    {
                        id: "shop-the-look-1",
                        type: "shop-the-look",
                        visible: true,
                        settings: {
                            surface: "alt",
                            eyebrow: { ar: "غرفة التنسيق", en: "Styling room" },
                            title: { ar: "اشتري الإطلالة", en: "Shop the look" },
                            subtitle: { ar: null, en: null },
                            headingLevel: 2,
                            heroImage: null,
                            heroTitle: { ar: null, en: null },
                            heroCaption: { ar: null, en: null },
                            heroCtaLabel: { ar: "تسوق الإطلالة", en: "Shop the look" },
                            heroHref: "",
                            productIds: "$products:4",
                        },
                    },
                    pearlRow(3, 2),
                    // #7 — `ab-comparison-section ab-comparison--square`. This is the
                    // DRAG SLIDER (`ab-comparison-slider` with `ab-comp-image-label`
                    // pairs), not the compare panel four other families ship, so it maps
                    // to `before-after` / `slider`.
                    //
                    // MEASURED: the frame is 16:9 at >=900 (800x450, their
                    // `--comp-max-width: 800px`) and 5:4 below (720x576 at 768,
                    // 358x286 at 390). Two of those three do not carry over; see the
                    // honest-differences list.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "slider",
                            shape: "square",
                            orientation: "horizontal",
                            surface: "alt",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "شوف الفرق", en: "See the Transformation" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    pearlRow(4, 3),
                    // #9 — `runway-hero runway-hero--center`, MID-PAGE. `RunwayVariant`
                    // is a port of this exact block and names pearl as one of the five
                    // engines shipping it. It ships as `banner-runway` rather than a
                    // second hero because pearl's page-leading hero is the `ab-slider`
                    // and `hero.maxPerTemplate` is 1 — the same resolution volta reached.
                    //
                    // MEASURED inline: `--runway-min-h: 72vh` (648px at a 900px viewport,
                    // constant at all eight widths), `--runway-overlay: 0.42`,
                    // `--runway-text: #FAF7F2`, `--runway-accent: #C9A962`, CENTRED, and
                    // TWO buttons (`--primary` and `--ghost`).
                    {
                        id: "banner-1",
                        type: "banner",
                        visible: true,
                        settings: {
                            variant: "banner-runway",
                            eyebrow: { ar: "موسم جديد", en: "New season" },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق التشكيلة", en: "Shop collection" },
                            // `banner.ctaHref` is an ENUM, not a URL — an empty string fails
                            // the save with `invalid_option`.
                            ctaHref: "collections",
                            image: null,
                            overlayOpacity: 42,
                            textAlign: "center",
                            headingLevel: 2,
                        },
                    },
                    // #10 — `ab-pgrid`: a real WRAPPING grid, ten cards over five columns
                    // at 1440 (269px each), four columns at 600, two at 390. 1013px at
                    // 1440. Their only stacking product block; the other three are
                    // scrollers.
                    {
                        id: "category-section-5",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "standard",
                            categoryId: "$category:4",
                            title: { ar: null, en: null },
                            showSectionTitle: true,
                            showViewAll: false,
                            productsPerSection: 10,
                            columnsPerRow: 5,
                            titleStyle: "plain",
                            titleAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #11 — `trust-promise trust-promise--soft`. Ported for this family
                    // as `features` / `feat-promise`; see the variant's own note in
                    // `FeatureRowSection.tsx` for the transcribed values.
                    //
                    // MEASURED: 130px at 1200/1440 (one row of four 251px cells inside a
                    // 1100px cap), 182px at 768 (two per row) and 331px at 390 (one per
                    // row). Their ground is `rgb(250,248,245)` — the page's warm
                    // off-white again, so `alt`.
                    //
                    // FOUR items, matching theirs. Their fourth reads "Secure payment";
                    // the first three are transcribed from their live copy as neutral
                    // placeholder, which is overwritten by the seller's own on apply.
                    {
                        id: "features-1",
                        type: "features",
                        visible: true,
                        settings: {
                            variant: "feat-promise",
                            surface: "alt",
                            title: { ar: null, en: null },
                            eyebrow: { ar: null, en: null },
                            headingLevel: 2,
                        },
                        blocks: [
                            ["truck", "شحن سريع", "Express delivery", "شحن سريع لكل العالم", "Express delivery worldwide"],
                            ["returns", "إرجاع مجاني", "Free returns", "إرجاع مجاني خلال ٣٠ يوم", "Free return within 30 days"],
                            ["headset", "خدمة العملاء", "Customer service", "متاحين طول أيام الأسبوع", "Available every day"],
                            ["lock", "دفع آمن", "Secure payment", "كل المعاملات مشفّرة", "Every transaction encrypted"],
                        ].map(([icon, tAr, tEn, dAr, dEn], i) => ({
                            id: `feat-1-${i}`,
                            type: "feature",
                            settings: {
                                icon,
                                title: { ar: tAr, en: tEn },
                                description: { ar: dAr, en: dEn },
                            },
                        })),
                    },
                    // #12 — `editorial-feature editorial-feature--light
                    // --img-left`. Already ported for avyro as `about` /
                    // `about-editorial`. MEASURED: media 549x686 and copy 523x255 at
                    // 1440 — the same box avyro recorded, so this is the identical block
                    // at the identical settings. `--img-left` is the DEFAULT side, so
                    // `imagePosition: "start"`. Ground `rgb(246,243,238)`, so `alt`.
                    {
                        id: "about-1",
                        type: "about",
                        visible: true,
                        settings: {
                            variant: "about-editorial",
                            surface: "alt",
                            eyebrow: { ar: "من داخل الورشة", en: "Inside the atelier" },
                            title: { ar: null, en: null },
                            body: { ar: null, en: null },
                            image: null,
                            imagePosition: "start",
                            ctaLabel: { ar: "اكتشف المزيد", en: "Discover more" },
                            ctaHref: "",
                            headingLevel: 2,
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // `ab-footer` — the two-column boutique archetype the footer survey
                    // assigned to luxury/pearl/peral: Shop and Pages columns on the start
                    // side, subscribe on the end, logo + socials, payments centred. The
                    // STRUCTURE matches exactly.
                    //
                    // No `surface` here: the footer schema declares none, so it would be
                    // inert. `footer-boutique` HARDCODES the light `alt` ground — its
                    // renderer comment reads "a LIGHT footer in the source (#f0f3f2 on
                    // #000)", which is luxury's, the store the shared variant was built
                    // for. But pearl-bags' own footer MEASURES pure BLACK (rgb(0,0,0),
                    // white ink). That divergence is a property of a shared, completed
                    // footer variant, recorded in the honest-differences list rather than
                    // re-architected for one family.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-boutique", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        // peral — family 11 of 13 (2026-07-20). NOT `pearl` (family 10): the two
        // differ by one transposed letter and share the `ab-*` engine, so their
        // tokens are nearly identical, but their PAGES are not. peral opens on an
        // `ab-slider` hero, runs its product rows around a SECOND slider used
        // MID-PAGE (`edition-hero-slider`, ported here as `banner`/`banner-edition`),
        // and closes on a `newsletter-luxe`; it ships no marquee, no trust bar and
        // no editorial feature — all of which pearl has.
        //
        // Reference: https://peral-gallery.myeasyorders.com. Capture
        // `scratchpad/fam-peral.json` (settled), measurements
        // `scratchpad/peral-measure*.mjs`.
        id: "gallery",
        name: { ar: "معرض", en: "Gallery" },
        description: {
            ar: "معرض فني هادئ — عناوين سيريف كبيرة وذهبي دافئ.",
            en: "A calm art gallery — large serif headings and warm gold.",
        },
        tokens: {
            colors: {
                // MEASURED on peral-gallery: the edition CTA, the shop-the-look CTA and
                // the newsletter submit all fill `rgb(201,169,98)` = `#c9a962` — the
                // same gold pearl resolved, corroborated three ways here. `accent`
                // grounds the accent band; peral's one authored accent is this gold, so
                // `accent == primary`, the resolution pearl/prime reached. `#cd1515`
                // IS on the page (the sale chip / discounted price in the editorial
                // card) but is a DISCOUNT signal, not a band — recorded as the tenth
                // preset caught before it could paint a red header.
                primary: "#c9a962",
                accent: "#c9a962",
                // MEASURED: body ground rgb(255,255,255), body ink rgb(19,19,22).
                surface: "#ffffff",
                ink: "#131316",
                // FIX PHASE B — the FOOTER BAND. The audit found 10 of 12 families on
                // the wrong footer hex because every archetype hard-bound the band to
                // a shared role (`accent` / ink / alt). MEASURED per family.
                // MEASURED footer rgb(34,34,34); ours rendered the light `alt` band.
                footerSurface: "#222222",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                // MEASURED: `Jost` computes on `.ab-plist-name` (16px w400 UPPERCASE
                // #212a2f) exactly as on pearl-bags — the same engine. Cairo for
                // Arabic, consistent with every family.
                headingEn: "Jost",
                bodyEn: "Jost",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "System Serif",
            },
            // MEASURED: `.ab-plist-card` / `.ab-plist-media` compute radius 0; the
            // gold CTAs and compare frame are all square. The pill on the edition CTA
            // is the variant's own decision.
            radius: "0",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "24px",
            pageGutterMobile: "16px",
            // VERIFIED against the measured card, not assumed: media aspect 1/1.2,
            // name 16px w400 UPPERCASE #212a2f, sale price #cd1515, radius 0 — the
            // `editorial` (EditorialCard) port, identical to pearl's.
            productCardStyle: "editorial",
            productCardRadius: "0",
        },
        // The ELEVENTH preset to carry page structure, conforming to the
        // `TemplateInstance` model avyro settled. Copy is neutral placeholder and
        // every image field is null, for the reasons avyro recorded. peral needs
        // exactly ONE new port — `banner`/`banner-edition` (the mid-page
        // `edition-hero-slider`) — and maps everything else to variants earlier
        // families ported.
        //
        // FOUR categories, in RAIL order (Tableaux, Sculptures, Antiques, Green
        // Plant Pot), so the `collections`/`portrait` rail matches theirs; the
        // product rows bind to whichever `$category:N` slot holds the right one, so
        // the rail order and the row order are decoupled:
        //   slot 0 Tableaux        -> #2, `slider`
        //   slot 1 Sculptures      -> #8, the 10-card `standard` grid
        //   slot 2 Antiques        -> #4, `slider`
        //   slot 3 Green Plant Pot -> #6, the `hero-split` featured row
        template: {
            home: {
                sections: [
                    // MEASURED: `.ab-header` computes `rgba(0,0,0,0)` with WHITE ink,
                    // position fixed — it floats transparently over the opening slider.
                    // `ink` keeps that light-on-dark reading; `accent` would paint it
                    // gold. Same call pearl made.
                    { id: "header", type: "header", locked: true, visible: true, settings: { variant: "luxe-centered", surface: "ink", transparentOverHero: true, mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false } },
                    // Declared OFF: peral has no value strip, and a section the preset
                    // omits is merged back VISIBLE, plus `BuyerStoreHomepage` pins a
                    // SYNTHETIC strip after the hero when the list has no `value-strip`
                    // TYPE. Absent means "default", not "off". (avyro #4.)
                    { id: "value-strip-1", type: "value-strip", visible: false, settings: {} },
                    // #0 — `ab-slider`: FOUR full-bleed artwork slides over a progress
                    // bar, pause and prev/next arrows. `edition`, not a variant of its
                    // own — the slides carry no DOM copy (all baked into the artwork) and
                    // `EditionVariant` renders the whole slide as the link when a slide
                    // has a destination but no copy (its own comment names peral). Same
                    // fold pearl took.
                    //
                    // MEASURED: 1392x609 at 1440. `data-autoplay="5000"` (SECONDS in our
                    // schema, which caps 2-10). NO scrim — the artwork is the whole
                    // slide.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "edition",
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            autoplayInterval: 5,
                            textAlign: "start",
                            overlayOpacity: 0,
                            // The prev/next arrows this block's comment describes. Same
                            // `ab-slider` arrows configuration as pearl — verified
                            // byte-identical on peral-gallery.
                            sliderControls: "inline-arrows",
                        },
                        blocks: [1, 2, 3, 4].map((n) => ({
                            id: `hero-slide-${n}`,
                            type: "slide",
                            settings: {
                                image: null,
                                headline: { ar: null, en: null },
                                subhead: { ar: null, en: null },
                                ctaLabel: { ar: null, en: null },
                                ctaHref: "",
                            },
                        })),
                    },
                    // #1 — `ab-categories`: four 4:5 portrait tiles with the name set
                    // BELOW the image, in a row. `portrait` — `BuyerCollectionGrid`
                    // documents that variant as a port of "the `ab-cat-card` portrait row
                    // (luxury, pearl, peral)", i.e. of THIS family's own row. pearl fixed
                    // its ladder; peral inherits it.
                    //
                    // MEASURED: heading "Shop by collection" 48px centred-in-textbox but
                    // START-aligned on the page (pearl's fix), NAVY #313e61 (a section
                    // colour the token model cannot express — departure); cards aspect
                    // 4/5, label 18px start-aligned.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "portrait",
                            showSectionTitle: true,
                            title: { ar: "تسوق حسب المجموعة", en: "Shop by collection" },
                            eyebrow: { ar: null, en: null },
                            headingLevel: 2,
                        },
                    },
                    // #2 — `ab-plist` "Tableaux": a 6-card scroller, start-aligned title
                    // with "View All" opposite. `category-sections`/`slider`, bound to
                    // slot 0 (Tableaux). Title left null so it falls back to the
                    // category name, which is what theirs prints.
                    {
                        id: "category-section-1",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "slider",
                            categoryId: "$category:0",
                            title: { ar: null, en: null },
                            showSectionTitle: true,
                            showViewAll: true,
                            productsPerSection: 6,
                            titleStyle: "plain",
                            titleAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #3 — `ab-comparison-section ab-comparison--square`: the DRAG SLIDER
                    // (`ab-comparison-slider` + `ab-comp-image-label` pairs), not the
                    // static compare panel — `before-after`/`slider`.
                    //
                    // MEASURED: 16:9 at >=900 (800x450, their `--comp-max-width: 800px`)
                    // and 5:4 below. Pill labels "Before"/"After". Ground is the page's
                    // warm off-white, so `alt`.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "slider",
                            shape: "square",
                            orientation: "horizontal",
                            surface: "alt",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "شوف الفرق", en: "See the Transformation" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    // #4 — `ab-plist` "Antiques": second 6-card scroller, bound to slot 2
                    // (Antiques).
                    {
                        id: "category-section-2",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "slider",
                            categoryId: "$category:2",
                            title: { ar: null, en: null },
                            showSectionTitle: true,
                            showViewAll: true,
                            productsPerSection: 6,
                            titleStyle: "plain",
                            titleAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #5 — `edition-hero-slider`, MID-PAGE. The SAME engine the hero
                    // ships as `edition`, ported here as `banner`/`banner-edition` because
                    // peral's page-leading hero is the `ab-slider` and
                    // `hero.maxPerTemplate` is 1 — the resolution volta/pearl reached for
                    // their mid-page runway.
                    //
                    // MEASURED: 1440x648 (72vh), CENTRED, gold CTA #c9a962, NO scrim. The
                    // banner ships no `slide` blocks, so it renders as a single static
                    // edition frame — the multi-slide carousel is the one departure.
                    {
                        id: "banner-1",
                        type: "banner",
                        visible: true,
                        settings: {
                            variant: "banner-edition",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق الآن", en: "Shop now" },
                            // `banner.ctaHref` is an ENUM, not a URL — empty string fails the
                            // save with `invalid_option`.
                            ctaHref: "collections",
                            image: null,
                            overlayOpacity: 0,
                            textAlign: "center",
                            headingLevel: 2,
                        },
                    },
                    // #6 — `ab-featured-products` "Green Plant Pot": an overlay-poster
                    // banner cell beside a horizontal product scroller at ALL widths.
                    // `hero-split`, the mapping pearl settled by ELIMINATION — the only
                    // featured variant that stays a single-row side-by-side scroller at
                    // every width. Bound to slot 3 (Green Plant Pot). Departures (split
                    // card vs overlay poster, red pill vs plain link) recorded, same as
                    // pearl.
                    {
                        id: "category-section-3",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "hero-split",
                            categoryId: "$category:3",
                            title: { ar: null, en: null },
                            showSectionTitle: true,
                            showViewAll: true,
                            showBanner: true,
                            bannerImage: null,
                            bannerEyebrow: { ar: null, en: null },
                            // The category name is the banner's copy — a real label, so it
                            // lives in the preset (only the photo is overlaid at preview).
                            bannerTitle: { ar: "نباتات وأصص", en: "Green Plant Pot" },
                            bannerSubtitle: { ar: null, en: null },
                            bannerCtaLabel: { ar: "عرض الكل", en: "View All" },
                            productsPerSection: 6,
                            titleStyle: "plain",
                            titleAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #7 — `shop-the-look--cream`, already ported for avyro. A cream-ground
                    // section: gold eyebrow, serif title, a left hero visual with a gold
                    // "SHOP FULL LOOK" pill and a right column of linked product cards.
                    // MEASURED 799px at 1440, ground rgb(243,239,232) = `alt`, three
                    // linked picks.
                    {
                        id: "shop-the-look-1",
                        type: "shop-the-look",
                        visible: true,
                        settings: {
                            surface: "alt",
                            eyebrow: { ar: "غرفة التنسيق", en: "Styling room" },
                            title: { ar: "اشتري الإطلالة", en: "Shop the look" },
                            subtitle: { ar: null, en: null },
                            headingLevel: 2,
                            heroImage: null,
                            heroTitle: { ar: null, en: null },
                            heroCaption: { ar: null, en: null },
                            heroCtaLabel: { ar: "تسوق الإطلالة", en: "Shop the look" },
                            heroHref: "",
                            productIds: "$products:3",
                        },
                    },
                    // #8 — `ab-pgrid` "Sculptures": a real WRAPPING grid, ten cards over
                    // five columns at 1440, CENTRED title with a "View All" button below.
                    // `category-sections`/`standard`, bound to slot 1 (Sculptures). This
                    // is peral's only stacking product block; the other three are
                    // scrollers.
                    {
                        id: "category-section-4",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "standard",
                            categoryId: "$category:1",
                            title: { ar: null, en: null },
                            showSectionTitle: true,
                            showViewAll: true,
                            productsPerSection: 10,
                            columnsPerRow: 5,
                            titleStyle: "plain",
                            titleAlign: "center",
                            headingLevel: 2,
                        },
                    },
                    // #9 — `newsletter-luxe`: a centred column, serif title, input + gold
                    // submit, fineprint, on a muted grey ground (rgb(123,123,121), a
                    // section colour the token model cannot express — departure).
                    // `news-center`, the mapping nature/blast settled.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-center",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اشترك في نشرتنا", en: "Subscribe to our newsletter" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                            image: null,
                            fineprint: { ar: null, en: null },
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // `ab-footer` — the two-column boutique archetype the footer survey
                    // assigned to luxury/pearl/peral. STRUCTURE matches (Shop/Pages
                    // columns, subscribe, logo + socials, payments, copy). MEASURED
                    // ground rgb(34,34,34) — near-black; `footer-boutique` HARDCODES the
                    // light `alt` ground (built for luxury), so the dark ground is a
                    // property of a shared variant, recorded in the honest list rather
                    // than re-architected. Same gap pearl recorded.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-boutique", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        id: "rounded",
        name: { ar: "دائري", en: "Rounded" },
        description: {
            ar: "بلاطات حجرية بحواف دائرية وزر إضافة.",
            en: "Filled stone tiles with rounded corners and an Add pill.",
        },
        tokens: {
            colors: {
                // MEASURED on phoenix-avyro: the plist "VIEW ALL" outline, product
                // names and card frames all compute rgb(33,42,47) = #212a2f — the dark
                // "stone" that is phoenix's identity. Kept as `primary`. The gold
                // #c9a962 that appears on the shop-the-look and newsletter CTAs is
                // EasyOrders' section-local accent (--stl-accent / the luxe submit), not
                // the store's primary — recorded as a departure below.
                primary: "#212a2f",
                // CORRECTED from the unmeasured #c94a4a. `accent` is a BAND colour in
                // our token model. MEASURED, #c94a4a is only the SALE-BADGE red
                // (rgb(201,74,74)) / discounted-price signal — it appears nowhere as a
                // band: phoenix's header is white (rgb(255,255,255)) and its footer is
                // rgb(33,33,33). Applying the red would have painted an accent-band
                // header/footer red, the same defect avyro caught. Set to the measured
                // footer near-black. (Note: `footer-accordion` actually grounds on the
                // INK band, so the footer tracks `ink`; `accent` is near-unused on this
                // page once the header is `surface: "page"` — this value is the safe
                // dark so no accent-band element ever paints red.)
                accent: "#212121",
                // MEASURED: the tile/card/Add-pill ground computes rgb(245,244,242) =
                // #f5f4f2 — the off-white "stone".
                surface: "#f5f4f2",
                // MEASURED: product names / body ink rgb(33,42,47).
                ink: "#212a2f",
                // FIX PHASE A — the PAGE GROUND, distinct from `surface`. Their
                // content_container paints this and every section is transparent over
                // it, so cards/photos read as raised panels. Only 5 of 12 families
                // have a non-white ground; the other 7 deliberately omit this token.
                pageGround: "#ECE9E2",
                // FIX PHASE B — the FOOTER BAND. The audit found 10 of 12 families on
                // the wrong footer hex because every archetype hard-bound the band to
                // a shared role (`accent` / ink / alt). MEASURED per family.
                // MEASURED footer rgb(33,33,33); `footer-accordion` grounded on ink #212a2f.
                footerSurface: "#212121",
                // PHOENIX PASS 1 — the HEADER BAND. MEASURED rgb(255,255,255): the bar
                // is plain WHITE over the #ECE9E2 page ground. A surface SLOT cannot
                // express that (`page` IS the cream ground, which is what the bar was
                // painting), so the header gets the same explicit band token the
                // footer got in Phase B.
                headerSurface: "#ffffff",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                headingEn: "Jost",
                bodyEn: "Jost",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "System Serif",
            },
            radius: "0.875rem",
            // PHOENIX PASS 1 — MEASURED: the header logo is an IMAGE WORDMARK,
            // 160x32 `object-fit: contain`, with NO store-name text node beside it.
            // `none` still rendered the platform's 28px avatar tile + the name.
            logoTreatment: "wordmark",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "10px",
            pageGutterMobile: "10px",
            // PHOENIX PASS 1 — the INSET-BAND treatment. `pageGutter` already insets
            // every full-bleed band by 10px; this is the other half, the 12px corner
            // MEASURED on the hero slider, the runway band and (a later pass) the
            // newsletter. One token, not three per-section patches.
            bandRadius: "12px",
            // MEASURED across a 600/900/1200 viewport-height sweep on the source: the
            // hero slider is 85vh (510/765/1020) and the mid-page runway band 72vh
            // (432/648/864) — both viewport-relative, not fixed pixels. The variant
            // defaults are 80vh and the volta-measured 513px.
            heroHeight: "85vh",
            bandHeight: "72vh",
            // "Filled stone tiles with rounded corners and an Add pill": the framed
            // card at 14px radius, measured on phoenix-avyro.
            productCardStyle: "framed",
            productCardRadius: "14px",
        },
        // The TWELFTH preset to carry page structure, conforming to the
        // `TemplateInstance` model avyro settled. Copy is neutral placeholder and
        // every image field is null (the preview overlay in `phoenix-apply.mjs`
        // fills them, never the preset). phoenix needed NO new port — all twelve of
        // its sections map to variants earlier families ported (its engines are the
        // `ab-*` family plus the shared `runway-hero`, `shop-the-look`,
        // `editorial-feature` and `newsletter-luxe` blocks). It is the FOURTH family
        // to need no new section TYPE and the third to need no new variant at all.
        //
        // FOUR categories, in phoenix-avyro's pill-portrait RAIL order (Men Hoodies,
        // Men Sweatpants, Women Hoodies, Women Sweatpants):
        //   slot 0 Men Hoodies      -> #2 "Best Sellers" (hero-subtitle) AND #3 slider
        //   slot 1 Men Sweatpants   -> #5 banner-grid
        //   slot 2 Women Hoodies    -> #7 slider
        //   slot 3 Women Sweatpants -> #9 banner-grid
        template: {
            home: {
                sections: [
                    // MEASURED: phoenix's header is WHITE (rgb(255,255,255)) over the page
                    // ground. The header field defaults to the ACCENT band, so it is set
                    // to `page` explicitly — the same MEASURED override avyro's white
                    // header needed. Without it the accent near-black would paint a dark
                    // header under a white page.
                    // PHOENIX PASS 1 — MEASURED `ab-header`: a rounded RECTANGLE card
                    // (x=20, w=1400, y=19, h=48, radius 12, white, shadow 0 1px 4px
                    // rgba(0,0,0,.08)), NOT the 1200px-capsule `pill`. `sticky: false`
                    // is the other half of the archetype: theirs is `position: relative`
                    // and scrolls away with the page. `logoDisplay: "logo"` pairs with
                    // the `wordmark` logoTreatment (no store-name text node).
                    { id: "header", type: "header", locked: true, visible: true, settings: { variant: "floating-card", surface: "page", sticky: false, logoDisplay: "logo", mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false } },
                    // Declared OFF: phoenix ships no value strip, and a section the preset
                    // omits is merged back VISIBLE, plus `BuyerStoreHomepage` pins a
                    // SYNTHETIC strip after the hero when the list has no `value-strip`
                    // TYPE. Absent means "default", not "off". (avyro #4.)
                    { id: "value-strip-1", type: "value-strip", visible: false, settings: {} },
                    // #0 — `ab-slider`: FOUR full-bleed artwork slides over a progress bar,
                    // NO text overlay (all copy baked into the artwork — MEASURED
                    // `heroHasText: false`). `edition`, the same fold pearl/peral took:
                    // `EditionVariant` renders the whole slide as the link when it has a
                    // destination but no copy. MEASURED 765px tall at every width, no
                    // scrim.
                    //
                    // CORRECTED 2026-07-22: this said "pause and prev/next arrows".
                    // Re-measured on phoenix-avyro, `.ab-slider-controls` holds exactly
                    // TWO children — `.ab-slider-progress` and `.ab-slider-pause`. The
                    // arrows belong to the pearl/peral configuration of the same engine,
                    // not to phoenix. Hence `sliderControls: "inline"`, which selects the
                    // single-row, pause-only treatment; the default "rail" remains the
                    // `edition-hero-slider` layout that dark/luxury/volta render.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "edition",
                            sliderControls: "inline",
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            autoplayInterval: 5,
                            textAlign: "start",
                            overlayOpacity: 0,
                        },
                        blocks: [1, 2, 3, 4].map((n) => ({
                            id: `hero-slide-${n}`,
                            type: "slide",
                            settings: {
                                image: null,
                                headline: { ar: null, en: null },
                                subhead: { ar: null, en: null },
                                ctaLabel: { ar: null, en: null },
                                ctaHref: "",
                            },
                        })),
                    },
                    // #1 — `ab-categories` (`ab-cat-card` + `ab-cat-track--grid`): four
                    // outlined-pill portrait tiles with the label centred, "Shop by
                    // collection" above. `pill-portrait` — phoenix's OWN category engine,
                    // ported in the categories workstream, and this is the first preset to
                    // use it (see `SELF_RESPONSIVE_COLLECTION_VARIANTS`, now measured).
                    // Renders the store's full category list, so it shows the four pills.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "pill-portrait",
                            showSectionTitle: true,
                            title: { ar: "تسوق حسب المجموعة", en: "Shop by collection" },
                            eyebrow: { ar: null, en: null },
                            headingLevel: 2,
                        },
                    },
                    // #2 — `ab-featured-products` "Best Sellers": a WIDE overlay-poster
                    // banner (565px at 1440) with an eyebrow/subtitle/CTA, BESIDE a
                    // wrapping product grid. `hero-subtitle` — the only featured variant
                    // that is a wide banner column beside a GRID (not a scroller) carrying
                    // its own subtitle + filled CTA, which is exactly the
                    // `ab-featured-hero-subtitle` markup. MEASURED section title 48px w400
                    // CENTRED = `titleStyle: display` + `titleAlign: center`. Bound to slot
                    // 0; "Best Sellers" is a curated collection, so the section keeps its
                    // own title while drawing Men Hoodies products (placeholder).
                    {
                        id: "category-sections-featured",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "hero-subtitle",
                            categoryId: "$category:0",
                            // "منتجاتنا", not "الأكثر مبيعاً": a theme title is static text,
                            // and a store with no sales must not be told it has best
                            // sellers (PR #212 audit §9, S8). The reference's "Best Sellers"
                            // is a curated collection; this section draws a category.
                            title: { ar: "منتجاتنا", en: "Our products" },
                            titleStyle: "display",
                            titleAlign: "center",
                            // PHOENIX PASS 2 — MEASURED `ab-featured-hero`: 565x929 radius
                            // 16, i.e. it FILLS the grid-row height (470 + 8 + 451), where
                            // the variant's blast-measured default caps at 640. The wrap and
                            // grid also run an 8px gap here against blast's 48/20.
                            bannerStyle: "scrim-fill",
                            showSectionTitle: true,
                            showBanner: true,
                            bannerImage: null,
                            bannerEyebrow: { ar: null, en: null },
                            bannerTitle: { ar: null, en: null },
                            bannerSubtitle: { ar: null, en: null },
                            bannerCtaLabel: { ar: "عرض الكل", en: "View all" },
                            productsPerSection: 6,
                            headingLevel: 2,
                        },
                    },
                    // #3 — `ab-plist` "MEN HOODIES": a 6-card scroller, small uppercase
                    // title with "VIEW ALL" opposite. `category-sections`/`slider`, bound
                    // to slot 0. MEASURED title 16px w500 uppercase left — nearest is
                    // `plain` + `start` (the departure: plain is w400 no-transform); the
                    // uppercase is a CSS treatment on the category name.
                    {
                        id: "category-sections-1",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "slider",
                            categoryId: "$category:0",
                            title: { ar: null, en: null },
                            showSectionTitle: true,
                            showViewAll: true,
                            productsPerSection: 8,
                            // PHOENIX PASS 2 — MEASURED `ab-plist-title`: 16px w500 UPPERCASE
                            // ls 1.28 with a 2px rule, NOT the 28px/w400 `plain` this shipped
                            // with. The pill + arrow pair is the other half of the same
                            // 40px `ab-plist-header` row.
                            titleStyle: "rule",
                            titleAlign: "start",
                            headerControls: "pill-arrows",
                            headingLevel: 2,
                        },
                    },
                    // #4 — `ab-comparison-section ab-comparison--square`: the DRAG SLIDER
                    // (After/Before pill labels), not the static compare panel.
                    // `before-after`/`slider`, `shape: square`. Ground is the page's
                    // off-white, so `alt`.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "slider",
                            shape: "square",
                            orientation: "horizontal",
                            surface: "alt",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "شوف الفرق", en: "See the Transformation" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    // #5 — `ab-pgrid` "MEN SWEATPANTS": a wrapping grid whose FIRST CELL is
                    // an overlay-poster promo tile at product-card size (343px), then 7
                    // product cards fill two rows of four. `category-sections`/`banner-grid`
                    // — the variant that puts the banner in the first grid cell at card
                    // size, its structural signature. Bound to slot 1, columnsPerRow 4,
                    // showSectionTitle OFF (the heading lives in the banner cell).
                    {
                        id: "category-sections-2",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "banner-grid",
                            categoryId: "$category:1",
                            title: { ar: null, en: null },
                            showSectionTitle: false,
                            showBanner: true,
                            // PHOENIX PASS 2 — MEASURED `ab-pgrid-hero`: the cell takes the
                            // ROW height (343x413 then 343x547), radius 14, a dark bottom
                            // gradient and its copy stacked bottom-start over a full-width
                            // CTA bar. The track gap is 16, not the ported 32.
                            bannerStyle: "scrim-fill",
                            bannerImage: null,
                            bannerEyebrow: { ar: null, en: null },
                            bannerTitle: { ar: null, en: null },
                            bannerSubtitle: { ar: null, en: null },
                            bannerCtaLabel: { ar: "عرض الكل", en: "View all" },
                            columnsPerRow: 4,
                            productsPerSection: 7,
                            titleStyle: "spaced",
                            titleAlign: "center",
                            headingLevel: 2,
                        },
                    },
                    // #6 — `shop-the-look--cream` (already ported for avyro): a cream-ground
                    // section — gold eyebrow, serif title, left hero visual with a "SHOP
                    // FULL LOOK" pill and a right column of linked product cards. MEASURED
                    // 799px at 1440, ground = `alt`, three linked picks.
                    {
                        id: "shop-the-look-1",
                        type: "shop-the-look",
                        visible: true,
                        settings: {
                            surface: "alt",
                            eyebrow: { ar: "غرفة التنسيق", en: "Styling room" },
                            title: { ar: "اشتري الإطلالة", en: "Shop the look" },
                            subtitle: { ar: null, en: null },
                            headingLevel: 2,
                            heroImage: null,
                            heroTitle: { ar: null, en: null },
                            heroCaption: { ar: null, en: null },
                            heroCtaLabel: { ar: "تسوق الإطلالة", en: "Shop the look" },
                            heroHref: "",
                            productIds: "$products:3",
                        },
                    },
                    // #7 — `ab-plist` "WOMEN HOODIES": second 6-card scroller, bound to slot
                    // 2.
                    {
                        id: "category-sections-3",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "slider",
                            categoryId: "$category:2",
                            title: { ar: null, en: null },
                            showSectionTitle: true,
                            showViewAll: true,
                            productsPerSection: 8,
                            // PHOENIX PASS 2 — MEASURED `ab-plist-title`: 16px w500 UPPERCASE
                            // ls 1.28 with a 2px rule, NOT the 28px/w400 `plain` this shipped
                            // with. The pill + arrow pair is the other half of the same
                            // 40px `ab-plist-header` row.
                            titleStyle: "rule",
                            titleAlign: "start",
                            headerControls: "pill-arrows",
                            headingLevel: 2,
                        },
                    },
                    // #8 — `runway-hero runway-hero--center`, MID-PAGE. The hero's `runway`
                    // engine used as a promotional band, ported here as `banner`/
                    // `banner-runway` (volta's port) because phoenix's page-leading hero is
                    // the `ab-slider`/`edition` and `hero.maxPerTemplate` is 1. MEASURED
                    // 648px at every width (the variant pins its heights), centred.
                    {
                        id: "banner-1",
                        type: "banner",
                        visible: true,
                        settings: {
                            variant: "banner-runway",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق التشكيلة", en: "Shop collection" },
                            // `banner.ctaHref` is an ENUM, not a URL — empty string fails the
                            // save with `invalid_option`.
                            ctaHref: "collections",
                            image: null,
                            overlayOpacity: 55,
                            textAlign: "center",
                            headingLevel: 2,
                        },
                    },
                    // #9 — `ab-pgrid` "WOMEN SWEATPANTS": second banner-grid, bound to slot
                    // 3.
                    {
                        id: "category-sections-4",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "banner-grid",
                            categoryId: "$category:3",
                            title: { ar: null, en: null },
                            showSectionTitle: false,
                            showBanner: true,
                            // PHOENIX PASS 2 — MEASURED `ab-pgrid-hero`: the cell takes the
                            // ROW height (343x413 then 343x547), radius 14, a dark bottom
                            // gradient and its copy stacked bottom-start over a full-width
                            // CTA bar. The track gap is 16, not the ported 32.
                            bannerStyle: "scrim-fill",
                            bannerImage: null,
                            bannerEyebrow: { ar: null, en: null },
                            bannerTitle: { ar: null, en: null },
                            bannerSubtitle: { ar: null, en: null },
                            bannerCtaLabel: { ar: "عرض الكل", en: "View all" },
                            columnsPerRow: 4,
                            productsPerSection: 7,
                            titleStyle: "spaced",
                            titleAlign: "center",
                            headingLevel: 2,
                        },
                    },
                    // #10 — `editorial-feature editorial-feature--light --img-left`
                    // (already ported for avyro): eyebrow, serif title, body, over an image
                    // on the START side. `about`/`about-editorial`, `imagePosition: start`.
                    // MEASURED 830px at 1440.
                    {
                        id: "about-1",
                        type: "about",
                        visible: true,
                        settings: {
                            variant: "about-editorial",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "قصتنا", en: "Our story" },
                            body: { ar: null, en: null },
                            image: null,
                            imagePosition: "start",
                            ctaLabel: { ar: null, en: null },
                            ctaHref: "",
                            headingLevel: 2,
                        },
                    },
                    // #11 — `newsletter-luxe`: a centred column, serif title, input + gold
                    // submit, fineprint. `news-center`, the mapping nature/blast/peral
                    // settled. MEASURED 357px at 1440.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-center",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اشترك في نشرتنا", en: "Subscribe to our newsletter" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                            image: null,
                            fineprint: { ar: null, en: null },
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // `ab-footer` — the subscribe-led accordion archetype the footer survey
                    // assigned to phoenix alone. `footer-accordion`: subscribe + socials on
                    // the start side, three `<details>` columns on the end side, collapsing
                    // to real accordions below 600px. MEASURED ground rgb(33,33,33) — it
                    // grounds on the INK band, which `ink` (#212a2f) reproduces closely.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-accordion", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        id: "grand",
        name: { ar: "فخم", en: "Grand" },
        description: {
            ar: "أسماء بارزة على بلاطات منتجات بحواف خفيفة.",
            en: "Bold capitalised names on lightly rounded product tiles.",
        },
        tokens: {
            colors: {
                // MEASURED on volta-bags (2026-07-20): their `:root` carries
                // `--color-primary: 0, 0, 0` and `--buy-btn-bg: #000`, and every
                // section heading computes to rgb(0,0,0) — the category rail at
                // 48px/600, all four product rows at 40px/700. Pure black, not #151515.
                primary: "#000000",
                // MEASURED, replacing the unmeasured `#cd1515`. This is the FIFTH
                // preset caught carrying that red, and it is the same trap dark
                // recorded: `#cd1515` is volta's `--product-badge-bg`, i.e. the
                // DISCOUNT BADGE colour — a price signal, not a surface. Applied as
                // the band it paints the header and every accent section bright red
                // against a white page.
                //
                // Their actual band is BLACK, corroborated on their page: the
                // newsletter section is authored inline `background: #000; color:
                // #FFF`, and the footer is `--ft-bg: #1F1F1F`.
                accent: "#000000",
                // MEASURED: body ground is rgb(255,255,255).
                surface: "#ffffff",
                // Their body ink computes to rgb(19,19,22); the 2-unit difference from
                // #151515 is imperceptible, so the existing value is kept — the same
                // call dark made on the same measurement.
                ink: "#151515",
                // FIX PHASE B — the FOOTER BAND. The audit found 10 of 12 families on
                // the wrong footer hex because every archetype hard-bound the band to
                // a shared role (`accent` / ink / alt). MEASURED per family.
                // MEASURED --ft-bg: #1F1F1F; ours grounded on #151515.
                footerSurface: "#1f1f1f",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                // MEASURED: their stylesheet loads Jost 400–800 and every heading
                // computes to Jost. Already correct; left alone.
                headingEn: "Jost",
                bodyEn: "Jost",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "Jost",
            },
            radius: "0.375rem",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "48px",
            pageGutterMobile: "20px",
            // Already correct and left alone. `BoldTileCard` documents itself as a
            // port of "the EasyOrders volta / dark engine's collection-grid card
            // (`ab-pgrid-card`)" — THIS family's card, and the one it is named for.
            // Confirmed against their live rows: 1:1 media at 311×311 (carousels)
            // and 322.5×322.5 (grid), border-radius 6.
            productCardStyle: "bold-tile",
            productCardRadius: "6px",
        },
        // The SIXTH preset to carry page structure (2026-07-20), conforming to the
        // `TemplateInstance` model avyro settled and elite/nature/sharp/dark
        // confirmed. Section order, variants and settings are the measured
        // volta-bags homepage — see the "volta" subsection of
        // docs/plans/2026-07-19-storefront-section-vocabulary.md.
        //
        // Copy is NEUTRAL PLACEHOLDER and every image field is null, for the
        // reasons avyro recorded: text is a CONTENT field so the seller's own copy
        // is carried over a theme's on apply, and `applyThemeToStore` blanks theme
        // images regardless.
        //
        // Volta is 12 sections and needs FIVE categories — a five-tile rail plus
        // four product rows drawn from four different categories — so its
        // reference store ships five, like dark's. `$category:N` DROPS its whole
        // section when unresolvable, so a four-category store would silently lose
        // the `banner-grid` row and one rail tile.
        template: {
            home: {
                sections: [
                    // Their `#eo-header` computes to `rgba(0,0,0,0)` over the white page
                    // ground. Our `header.surface` defaults to the ACCENT band, and this
                    // preset's accent is their black, so leaving it unset paints a black
                    // header under a white page. Same call as avyro, elite and dark.
                    { id: "header", type: "header", locked: true, visible: true, settings: { variant: "grand-centered", surface: "page", transparentOverHero: true, mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false } },
                    // #0 — `edition-hero-slider`, a TWO-slide crossfade (dark's is three).
                    // MEASURED `--eh-h-dt: 72` / `--eh-h-mb: 75` (svh) = 648px at 900+
                    // and 675 below, and `--eh-overlay: 0.45`.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "edition",
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            // SECONDS, not the milliseconds their `data-autoplay` carries.
                            // The schema range is 2–10 and the validator rejects anything
                            // else.
                            autoplayInterval: 6,
                            textAlign: "start",
                            overlayOpacity: 45,
                        },
                        blocks: [1, 2].map((n) => ({
                            id: `hero-slide-${n}`,
                            type: "slide",
                            settings: {
                                image: null,
                                headline: { ar: null, en: null },
                                subhead: { ar: null, en: null },
                                ctaLabel: { ar: null, en: null },
                                ctaHref: "",
                            },
                        })),
                    },
                    // volta ships NO trust strip. Present-and-hidden rather than omitted,
                    // because `BuyerStoreHomepage` pins a SYNTHETIC value strip after the
                    // hero whenever the list contains no `value-strip` TYPE at all.
                    { id: "value-strip-1", type: "value-strip", visible: false, settings: {} },
                    // #1 — `ab-categories`: a heading over a scrollable row of five
                    // SQUARE category cards, 320×320 at 1440, each a photo under a dark
                    // scrim with the label set large and centred ON the artwork.
                    //
                    // `scrim-tile`, NOT `rail`. `BuyerCollectionGrid` documents
                    // `scrim-tile` as the port of "the EasyOrders `ab-cat-card` (dark,
                    // volta)" — this family's own row, named in the renderer. The first
                    // draft copied dark's preset, which carries `rail`; that variant is
                    // the `category-section-wrapper` engine (nature) — a heading COLUMN
                    // on the leading edge beside a track of 4:5 cards with the label in a
                    // strip BELOW the photo. It measured +69% at 1440 and the screenshot
                    // showed why: our tiles were 4:5 with captions underneath and the
                    // whole track pushed right by a heading column theirs does not have.
                    //
                    // Caught by LOOKING at the page-level screenshot; the height delta
                    // alone would have read as a spacing problem.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "scrim-tile",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "التشكيلات", en: "Collections" },
                            headingLevel: 2,
                        },
                    },
                    // #2 — `ab-marquee-section--single`. Ported for dark; volta needed a
                    // FOURTH type size for it. MEASURED inline on their section:
                    // `--mq-bg: #FFF` / `--mq-color: #000` (so the band sits on the PAGE
                    // ground, not the accent band — the opposite of dark's),
                    // `--mq-font-size-dt: 180px` / `-mb: 70px`,
                    // `--mq-lh-dt: 150px` / `-mb: 100px` (= the measured section height at
                    // 900+ and below), `--mq-speed-dt: 35s`, `--mq-tilt: 0deg`.
                    {
                        id: "marquee-band-1",
                        type: "marquee-band",
                        visible: true,
                        settings: {
                            surface: "page",
                            size: "display",
                            speed: "medium",
                            direction: "start",
                            tilt: "none",
                            // MEASURED: their track is four bare `ab-marquee-item` spans with
                            // NO separator element between them — the phrase simply repeats.
                            // dark's band prints a dot, so this is per-family, not a default.
                            separator: "none",
                        },
                        blocks: [
                            {
                                id: "marquee-line-1-1",
                                type: "marquee-line",
                                settings: { text: { ar: null, en: null } },
                            },
                        ],
                    },
                    // #3 — `ab-plist`, 8 cards in a horizontal track (scrollWidth 2748
                    // against a 1440 viewport), title left with "View All" + arrows
                    // opposite.
                    voltaRow(1, 0, "slider", 8, "start"),
                    // #4 — `ab-comparison-section`. Their modifier is `--square`, and it
                    // is a lie for the second family running: the MEDIA MEASURES
                    // 1000×563 = 1.778, i.e. 16:9. Mapped from the BOX, so `wide`.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "panel",
                            aspect: "wide",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اختر لونك", en: "Choose your colour" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    // #5 — `ab-pgrid`, 8 cards in a 4-column grid, title CENTRED.
                    voltaRow(2, 2, "standard", 8, "center"),
                    // #6 — `shop-the-look--cream`, already ported for avyro. Four linked
                    // product cards beside a 649×540 hero visual.
                    {
                        id: "shop-the-look-1",
                        type: "shop-the-look",
                        visible: true,
                        settings: {
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اشتري الإطلالة", en: "Shop the look" },
                            subtitle: { ar: null, en: null },
                            headingLevel: 2,
                            heroImage: null,
                            heroTitle: { ar: null, en: null },
                            heroCaption: { ar: null, en: null },
                            heroCtaLabel: { ar: "تسوق الإطلالة", en: "Shop the look" },
                            heroHref: "",
                            productIds: "$products:4",
                        },
                    },
                    // #7 — the second `ab-plist`.
                    voltaRow(3, 1, "slider", 8, "start"),
                    // #8 — `rnw-hero rnw-hero--left`, MID-PAGE. This is the hero's
                    // `runway` engine used as a promotional band, which is why it ships
                    // here as the `banner-runway` variant rather than a second hero:
                    // volta's page-leading hero is `edition` and `hero.maxPerTemplate`
                    // is 1. Ported for this family.
                    //
                    // MEASURED: `--rnw-overlay: 68`, left-aligned, 513px from 768 up and
                    // 360 below (the variant pins those two heights itself).
                    {
                        id: "banner-1",
                        type: "banner",
                        visible: true,
                        settings: {
                            variant: "banner-runway",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق التشكيلة", en: "Shop collection" },
                            // `banner.ctaHref` is an ENUM, not a URL — an empty string fails
                            // the save with `invalid_option`. Their button points at a
                            // category; `collections` is the closest valid destination.
                            ctaHref: "collections",
                            image: null,
                            overlayOpacity: 68,
                            textAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #9 — `ab-featured-products`: a 327×500 category banner cell beside
                    // a carousel of the same-width product cards, which is our
                    // `banner-grid`.
                    //
                    // THREE products, not the eight their track holds — the same measured
                    // call dark recorded. Their section is a SINGLE 500px row: the banner
                    // cell plus three visible cards fills a row of four, and the
                    // remaining five are reached by scrolling. Our `banner-grid` WRAPS
                    // instead of scrolling, so eight products would stack into three
                    // rows and the section would measure ~3× theirs.
                    //
                    // Stated plainly, because it is a real difference and not a fix:
                    // ours shows four cells and stops where theirs shows four and
                    // scrolls to eight. The row HEIGHT matches; the reachable product
                    // count does not.
                    voltaRow(4, 4, "banner-grid", 3, "start"),
                    // #10 — `scroll-trans-v1-section`, ported for dark. MEASURED inline:
                    // `height: 150vh`, `--init-width: 240px` / `--init-height: 360px`
                    // (= `medium`), overlay gradient peaking at rgba(0,0,0,0.45).
                    //
                    // Their section is authored `background-color: rgb(255,255,255)` —
                    // the PAGE ground, where dark's same block sits on its near-black
                    // band. Same section, opposite surface, and only measuring it tells
                    // you which.
                    {
                        id: "scroll-reveal-1",
                        type: "scroll-reveal",
                        visible: true,
                        settings: {
                            image: null,
                            title: { ar: null, en: null },
                            subtitle: { ar: null, en: null },
                            headingLevel: 2,
                            surface: "page",
                            startSize: "medium",
                            overlayOpacity: 45,
                            textInside: null,
                            textOutside: null,
                        },
                    },
                    // #11 — `newsletter-luxe`, which nature mapped to the centred
                    // layout. Authored inline `background: #000; color: #FFF`, i.e. the
                    // accent band — dark's instance carried no surface at all.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-center",
                            surface: "accent",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اشترك في نشرتنا", en: "Subscribe to our newsletter" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // Their footer is `ab-footer ab-footer-v2` — the same engine dark
                    // runs — and it leads with "SUBSCRIBE TO OUR NEWSLETTER" above the
                    // SHOP / PAGES / CONTACT US columns, which is what `footer-wave`
                    // (Newsletter-led) is. NOT `footer-directory` (the `ps-footer`
                    // archetype avyro/elite/nature share).
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-wave", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        id: "noir",
        name: { ar: "داكن", en: "Noir" },
        description: {
            ar: "نفس تخطيط فولتا مع زر ليموني لمتجر ليلي.",
            en: "The Volta layout with a lime CTA for a night-mode storefront.",
        },
        tokens: {
            colors: {
                // MEASURED on dark-electronics (2026-07-20): every section heading,
                // the category-rail ink and the marquee type all compute to
                // rgb(198,255,52).
                primary: "#c6ff34",
                // MEASURED, replacing the unmeasured `#cd1515`. `accent` is a BAND
                // colour in our token model, and dark's band is corroborated three
                // independent ways on their page: the marquee is authored
                // `--mq-bg: #0B0C10`, the scroll-transition section carries
                // `background-color: rgb(11,12,16)` inline, and the review carousel is
                // `--sr-bg: #0B0C10`.
                //
                // Where the old red came from is worth recording, because it is the
                // same trap the avyro and elite notes flagged: `#cd1515` is the
                // DISCOUNT BADGE colour hardcoded inside `BoldTileCard` — a price
                // signal, not a surface. Applied as the band it painted the header and
                // every accent section bright red against a white page. Eight `eo-*`
                // presets still carry an unmeasured red accent.
                accent: "#0b0c10",
                // MEASURED: their body ground is rgb(255,255,255). Despite the family
                // name, dark is a WHITE page with near-black bands, not a dark theme.
                surface: "#ffffff",
                // Their body ink computes to rgb(19,19,22); the 2-unit difference from
                // #151515 is imperceptible, so the existing value is kept.
                ink: "#151515",
                // FIX PHASE A — the PAGE GROUND, distinct from `surface`. Their
                // content_container paints this and every section is transparent over
                // it, so cards/photos read as raised panels. Only 5 of 12 families
                // have a non-white ground; the other 7 deliberately omit this token.
                pageGround: "#0B0C10",
                // FIX PHASE B — the FOOTER BAND. The audit found 10 of 12 families on
                // the wrong footer hex because every archetype hard-bound the band to
                // a shared role (`accent` / ink / alt). MEASURED per family.
                // MEASURED footer rgb(19,21,28); headings + links are the lime.
                footerSurface: "#13151c",
                footerHeading: "#C6FF34",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                // MEASURED: every heading on their page computes to Jost — section
                // headings 48/600, product-row titles 40/700, the review carousel
                // 32/600 and the scroll-transition headline 88/800.
                headingEn: "Jost",
                bodyEn: "Jost",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "Jost",
            },
            radius: "0.375rem",
            logoTreatment: "none",
            // Headings paint the BRAND role, not the body ink. The hex is already
            // correct in `colors` above; only the role binding was wrong.
            headingRole: "primary",
            // Already correct and left alone. `BoldTileCard` documents itself as a
            // port of "the EasyOrders volta / dark engine's collection-grid card
            // (`ab-pgrid-card`)" — THIS family's card. Confirmed against their live
            // rows: 1:1 media at 311×311 and 323×323, radius 6.
            productCardStyle: "bold-tile",
            productCardRadius: "6px",
        },
        // The FIFTH preset to carry page structure (2026-07-20), conforming to the
        // `TemplateInstance` model avyro settled and elite/nature/sharp confirmed.
        // Section order, variants and settings are the measured dark-electronics
        // homepage — see the "dark" subsection of
        // docs/plans/2026-07-19-storefront-section-vocabulary.md.
        //
        // Copy is NEUTRAL PLACEHOLDER and every image field is null, for the
        // reasons avyro recorded: text is a CONTENT field so the seller's own copy
        // is carried over a theme's on apply, and `applyThemeToStore` blanks theme
        // images regardless.
        //
        // Dark is the DEEPEST family assembled so far — 17 sections against
        // avyro/elite's 10 — and it is the first whose page needs FIVE category
        // rows, which is why its reference store ships five categories.
        template: {
            home: {
                sections: [
                    // Their `ab-header` sits on the white page ground, not on the band.
                    // Same measured call as avyro and elite: our `header.surface`
                    // defaults to the ACCENT band, and this preset's accent is their
                    // near-black, so leaving it unset paints a black header under a
                    // white page.
                    { id: "header", type: "header", locked: true, visible: true, settings: { variant: "grand-centered", surface: "page", transparentOverHero: true, mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false } },
                    // #0 — `edition-hero-slider`, a 3-slide crossfade. 720px at >=900,
                    // 630 below. `edition` was already ported and its renderer
                    // (`EditionVariant.tsx`) names this exact engine.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "edition",
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            // SECONDS. The schema range is 2–10 and the validator rejects
                            // anything else; their `data-autoplay` is milliseconds.
                            autoplayInterval: 6,
                            textAlign: "start",
                            overlayOpacity: 25,
                        },
                        blocks: [1, 2, 3].map((n) => ({
                            id: `hero-slide-${n}`,
                            type: "slide",
                            settings: {
                                image: null,
                                headline: { ar: null, en: null },
                                subhead: { ar: null, en: null },
                                ctaLabel: { ar: null, en: null },
                                ctaHref: "",
                            },
                        })),
                    },
                    // dark ships NO trust strip. Present-and-hidden rather than omitted,
                    // because `BuyerStoreHomepage` pins a SYNTHETIC value strip after the
                    // hero whenever the list contains no `value-strip` TYPE at all. Same
                    // shim, same workaround as avyro and elite.
                    { id: "value-strip-1", type: "value-strip", visible: false, settings: {} },
                    // #1 — `ab-marquee-section`. NEW TYPE, ported for this family.
                    marqueeBand(1, "start"),
                    // #2 — `ab-categories`: a heading over a scrollable rail of square
                    // category cards (5 tiles, 320×320 at 1440).
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "rail",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "تسوق حسب الفئة", en: "Shop by category" },
                            headingLevel: 2,
                        },
                    },
                    // #3 — the second marquee. Theirs is `ab-marquee--arabic`, whose only
                    // rule change is `animation-name: marquee-scroll-ar` — i.e. it
                    // travels the OTHER WAY. Expressed as `direction: "end"` rather than
                    // as an Arabic-specific variant, because the direction is the actual
                    // difference and tying it to a locale would make the EN storefront
                    // render a different layout from the AR one.
                    marqueeBand(2, "end"),
                    // #4 / #6 — two `ab-plist` carousels.
                    darkRow(1, 0, "slider", 8),
                    // #5 — `home_banner`, a plain full-width promotional image.
                    darkBanner(1),
                    darkRow(2, 1, "slider", 7),
                    darkBanner(2),
                    // #8 / #10 — two `ab-pgrid` grids.
                    darkRow(3, 2, "standard", 7),
                    // #9 — `ab-comparison-section`. Their modifier is `--square`, but the
                    // MEDIA MEASURES 1000×563 = 1.778, i.e. 16:9 — so the mapped aspect
                    // is `wide`, not `squareish`. Reading the class name instead of the
                    // box would have set a 4:3 panel here.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "panel",
                            aspect: "wide",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "شوف الفرق", en: "See the transformation" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    darkRow(4, 3, "standard", 8),
                    // #11 — `shop-the-look--cream`, already ported for avyro.
                    {
                        id: "shop-the-look-1",
                        type: "shop-the-look",
                        visible: true,
                        settings: {
                            eyebrow: { ar: "تشكيلة جديدة", en: "New collection" },
                            title: { ar: "اشتري الإطلالة", en: "Shop the look" },
                            subtitle: { ar: null, en: null },
                            headingLevel: 2,
                            heroImage: null,
                            heroTitle: { ar: null, en: null },
                            heroCaption: { ar: null, en: null },
                            heroCtaLabel: { ar: "تسوق الإطلالة", en: "Shop the look" },
                            heroHref: "",
                            productIds: "$products:4",
                        },
                    },
                    // #12 — `scroll-trans-v1-section`. NEW TYPE, ported for this family.
                    // Their instance is 150vh with a 240×360 start box and a 0.45 scrim.
                    {
                        id: "scroll-reveal-1",
                        type: "scroll-reveal",
                        visible: true,
                        settings: {
                            image: null,
                            title: { ar: null, en: null },
                            subtitle: { ar: null, en: null },
                            headingLevel: 2,
                            // Their section carries `background-color: rgb(11,12,16)` inline
                            // — the same measured band as the marquee and the reviews.
                            surface: "accent",
                            startSize: "medium",
                            overlayOpacity: 45,
                            textInside: null,
                            textOutside: null,
                        },
                    },
                    // #13 — the third `home_banner`, a 1:1 square at 1344×1344.
                    darkBanner(3),
                    // #14 — `ab-featured-products`: a category banner slot beside the
                    // product cells, which is our `banner-grid`.
                    //
                    // THREE products, not the eight their row holds. Their section is a
                    // SINGLE ROW 545px tall — one card high — with the eight products in
                    // a horizontal scroller beside the banner slot. Our `banner-grid`
                    // wraps instead of scrolling, so eight products plus the banner cell
                    // came out as three rows of four and the section measured 1479
                    // against their 545 (+171%). Three products + the banner fills one
                    // row of four, which is the geometry theirs shows at rest.
                    //
                    // Stated plainly, because it is a real difference and not a fix:
                    // ours shows four cells and stops where theirs shows four and
                    // scrolls to eight. The row HEIGHT now matches; the reachable
                    // product count does not.
                    darkRow(5, 4, "banner-grid", 3),
                    // #15 — `sr-reviews`. NEW VARIANT, ported for this family.
                    {
                        id: "testimonials-1",
                        type: "testimonials",
                        visible: true,
                        settings: {
                            variant: "test-screenshots",
                            // MEASURED `--sr-bg: #0B0C10`.
                            surface: "accent",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "آراء عملائنا", en: "What customers are saying" },
                            description: { ar: null, en: null },
                            headingLevel: 2,
                        },
                        blocks: [1, 2, 3, 4].map((n) => ({
                            id: `review-shot-${n}`,
                            type: "testimonial",
                            settings: {
                                quote: { ar: null, en: null },
                                author: { ar: null, en: null },
                                role: { ar: null, en: null },
                                image: null,
                                rating: 5,
                            },
                        })),
                    },
                    // #16 — `newsletter-luxe`, which nature already mapped to the
                    // centred layout.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-center",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اشترك في نشرتنا", en: "Subscribe to our newsletter" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // Their `ab-footer-v2` leads its brand column with the newsletter
                    // subscribe form above the link columns, which is what
                    // `footer-wave` (Newsletter-led) is. NOT `footer-directory` — that is
                    // the `ps-footer` archetype avyro/elite/nature share, and dark runs a
                    // different footer engine.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-wave", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        id: "catalog",
        name: { ar: "كتالوج", en: "Catalog" },
        description: {
            ar: "كروت كتالوج بظل ناعم وصور محتواة.",
            en: "Soft-shadowed catalog cards with contained product shots.",
        },
        tokens: {
            colors: {
                // MEASURED on prime-books (2026-07-20): `:root` carries
                // `--color-primary: 15, 26, 60`. The same navy grounds the flash-sale
                // card (`--fsc-bg: #0F1A3C`), the newsletter button (`--nl-btn`), the
                // compare panel's labels and CTA (`--comp-label-color`,
                // `--comp-cta-color`) and the newsletter ink (`--nl-text`). It was
                // carrying `#ff0000`.
                primary: "#0f1a3c",
                // MEASURED, replacing the unmeasured `#ff0000`. This is the EIGHTH
                // preset caught carrying a discount signal as its accent band.
                // `#ff0000` is prime's `--product-badge-bg`, the sale-chip fill, and
                // it IS on the page — the featured hero's CTA is authored
                // `rgb(255 0 0)`. But `accent` is a BAND in our token model: it
                // grounds the header and every accent section, and prime's header
                // computes white with navy ink. Applied as a band, their button red
                // would paint the header pure red against a white page.
                //
                // Corroborated five ways that the band is the navy: `--color-primary:
                // 15,26,60`, `--fsc-bg: #0F1A3C`, `--nl-btn: #0F1A3C`,
                // `--comp-label-color: #0F1A3C` and `--comp-label-border-color`.
                accent: "#0f1a3c",
                // MEASURED: body ground computes rgb(255,255,255).
                surface: "#ffffff",
                // MEASURED: body ink computes rgb(19,19,22), not the #121212 this
                // carried. Same correction blast took.
                ink: "#131316",
                // FIX PHASE A — the PAGE GROUND, distinct from `surface`. Their
                // content_container paints this and every section is transparent over
                // it, so cards/photos read as raised panels. Only 5 of 12 families
                // have a non-white ground; the other 7 deliberately omit this token.
                pageGround: "#F9F9F9",
                // FIX PHASE B — the FOOTER BAND. The audit found 10 of 12 families on
                // the wrong footer hex because every archetype hard-bound the band to
                // a shared role (`accent` / ink / alt). MEASURED per family.
                // MEASURED footer #0F1A3C; ours grounded on ink #131316.
                footerSurface: "#0f1a3c",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                // MEASURED with the same caveat blast recorded: `document.body`
                // computes to the platform UI stack, which is only the unstyled
                // default. The AUTHORED faces are `Jost` for Latin and — read
                // directly off their marquee's own inline `--tsm-font: 'Cairo',
                // cursive` — Cairo for Arabic. Unchanged.
                headingEn: "Jost",
                bodyEn: "Jost",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "Newsreader",
            },
            // MEASURED: their compare panel and flash card sit at 10px
            // (`border-radius: 10px` on `.fsc-flash-sale__card` at >=769 and on
            // `.stl__slide`), and the product card media at 10px. Already correct.
            radius: "0.625rem",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "50px",
            pageGutterMobile: "20px",
            // FIX PHASE B — prime is the one audited family whose HERO is edge-to-edge
            // over a contained page. Phase A tied `--page-inset` to the gutter for
            // everyone, which inset prime's hero to 50px; this opts it back out
            // WITHOUT touching the 50px its sections carry.
            heroBleed: true,
            // Headings paint the BRAND role, not the body ink. The hex is already
            // correct in `colors` above; only the role binding was wrong.
            headingRole: "primary",
            productCardStyle: "catalog",
            productCardRadius: "10px",
        },
        // The NINTH preset to carry page structure (2026-07-20), conforming to the
        // `TemplateInstance` model avyro settled and elite/nature/sharp/dark/volta/
        // hype/blast confirmed. Section order, variants and settings are the
        // measured prime-books homepage — see the "prime" subsection of
        // docs/plans/2026-07-19-storefront-section-vocabulary.md.
        //
        // Copy is NEUTRAL PLACEHOLDER and every image field is null, for the
        // reasons avyro recorded.
        //
        // prime is 14 sections — the second-deepest family after nature and dark —
        // and needs SIX categories: a six-card circle rail, five tab panels and
        // four product rows. `$category:N` DROPS its whole section when
        // unresolvable, so a store with fewer categories silently loses rows.
        template: {
            home: {
                sections: [
                    // MEASURED: `.ab-header` computes white with navy ink over the white
                    // page ground. Our `header.surface` defaults to the ACCENT band, and
                    // this preset's accent is their navy, so leaving it unset paints a
                    // navy header under a white page. Same call as every family so far.
                    { id: "header", type: "header", locked: true, visible: true, settings: { variant: "search-prime", surface: "ink", mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: true } },
                    // Declared and switched OFF. A section the preset does not LIST is
                    // merged back in VISIBLE by `applyThemeToStore`, so omitting
                    // `value-strip` paints a trust strip prime-books does not have.
                    // Absent means "default", not "off" — the defect blast's page-level
                    // diff caught.
                    { id: "value-strip-1", type: "value-strip", visible: false, settings: {} },
                    // #0 — `ab-categories`, ABOVE the hero. prime is the second family
                    // (after blast) whose page does not open on a hero.
                    //
                    // MEASURED: SIX cards, each a circle with the name BENEATH it —
                    // 84px circles at >=1200, 62px below — which is what separates
                    // `avatar-chips` from blast's `avatar-row` (64px circle, name
                    // BESIDE). The section is 165px at 1200/1440 and 141/158 below.
                    // Their `.ab-categories-title` is present and EMPTY, so the rail
                    // carries no heading.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "avatar-chips",
                            showSectionTitle: false,
                            title: { ar: null, en: null },
                            eyebrow: { ar: null, en: null },
                            // NO `count`, deliberately, and the validator is what forced the
                            // question. `count` is capped 1–4 and prime's rail carries SIX
                            // categories, so `count: 6` failed `validateThemeJson` with
                            // `out_of_range` — the check the avyro note added after
                            // `autoplayInterval` shipped in the wrong unit.
                            //
                            // The cap was NOT raised, because `count` does not apply here:
                            // its own `showIf` scopes it to the six grid variants, and
                            // `BuyerCollectionGrid` hands `avatar-chips` the FULL category
                            // list exactly as it does `mini` and `bento`. The rail shows six
                            // because the store has six. (blast's preset carries an equally
                            // inert `count: 4` on `avatar-row`; it validates only because 4
                            // is inside the range, so nothing surfaced it there.)
                            headingLevel: 2,
                        },
                    },
                    // #1 — `rnw-hero rnw-hero--center`. MEASURED inline:
                    // `--rnw-h-dt: 75svh` (675 from 768 up), `--rnw-h-mb: 60svh` (540),
                    // `--rnw-overlay: 42`, `--rnw-t-dt: 60px` / `-mb: 36px`,
                    // `--rnw-b-rad: 99px`, `--rnw-accent: #FFF`. Identical geometry to
                    // blast's hero — the same runway engine at the same settings.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "runway",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق التشكيلة", en: "Shop collection" },
                            ctaHref: "all_products",
                            // MEASURED: their `rnw-hero__actions` holds exactly ONE
                            // `rnw-btn`. Left unset this defaults to `collections` and
                            // paints a second pill theirs does not have.
                            ctaHrefSecondary: "none",
                            image: null,
                            textAlign: "center",
                            overlayOpacity: 42,
                        },
                    },
                    // #2 — `tsm-scroll`, a scrolling word band. MEASURED inline:
                    // `--tsm-fs-dt: 32px` / `-mb: 22px`, `--tsm-weight: 700`,
                    // `--tsm-speed-dt: 30s`, `--tsm-tilt: 0deg`, `--tsm-bg` EMPTY (the
                    // band is transparent over the page, with a 1px bottom hairline).
                    // Section 135px at >=900, 123px below.
                    //
                    // `size: "small"` is their 32px desktop face EXACTLY. Two values do
                    // not match and are recorded rather than papered over: their mobile
                    // face is 22px against small's 18, and their line box is 35.2px
                    // (32 x 1.1) against small's 46. See the "what still differs" list.
                    {
                        id: "marquee-band-1",
                        type: "marquee-band",
                        visible: true,
                        settings: {
                            size: "small",
                            speed: "slow",
                            direction: "start",
                            tilt: "none",
                            // MEASURED: their separator is an IMAGE — a 70px
                            // `tsm-scroll__icon-img` between every phrase, and it is what
                            // drives the band's 135px height. Our separator control offers
                            // dot/slash/star/none only, and a preset ships no imagery
                            // regardless, so this takes the nearest mark. The band is
                            // consequently SHORTER than theirs; recorded as a known gap
                            // rather than approximated with a fake glyph.
                            separator: "dot",
                            // MEASURED: their phrases author `text-transform: none`.
                            textCase: "none",
                            surface: "page",
                        },
                        blocks: [
                            {
                                id: "marquee-line-1",
                                type: "marquee-line",
                                settings: { text: { ar: "أهلًا بيك في متجرنا", en: "Welcome to our store" } },
                            },
                        ],
                    },
                    // #3 — `mcp-multi-cat`: FIVE category tabs above one product
                    // carousel. The new TYPE ported for this family. MEASURED: the tabs
                    // row is 36px at >=900 and 26px below, the active panel carries 8
                    // cards, and the section is 396px at 1440.
                    //
                    // Their block prints NO title or subtitle — the tabs are the whole
                    // header — so both are null and the head block does not render.
                    {
                        id: "category-tabs-1",
                        type: "category-tabs",
                        visible: true,
                        settings: {
                            categoryIds: [
                                "$category:0",
                                "$category:1",
                                "$category:2",
                                "$category:3",
                                "$category:4",
                            ],
                            title: { ar: null, en: null },
                            subtitle: { ar: null, en: null },
                            headingLevel: 2,
                            productsPerTab: 8,
                            showViewAll: true,
                        },
                    },
                    // #4 — `fsc-flash-sale`: a contained card reading headline | four
                    // bare digit groups | a line of copy | a pill code button.
                    // MEASURED 140px at 1200/1440, 243px stacked at <=768. Ported here
                    // as `countdown` / `count-flash`.
                    //
                    // `targetDate` is deliberately null, for the reason blast recorded:
                    // a preset cannot ship a real date without it being wrong the day
                    // after the theme is applied. With no target the card prints its
                    // headline and copy and no digits, which is the honest empty state.
                    {
                        id: "countdown-1",
                        type: "countdown",
                        visible: true,
                        settings: {
                            variant: "count-flash",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "تخفيضات سريعة!", en: "Flash sale now on!" },
                            subhead: {
                                // No sales claim in static copy (audit §9, S8).
                                ar: "وفّر على منتجات مختارة",
                                en: "Save on selected products",
                            },
                            targetDate: null,
                            expiredMessage: { ar: "العرض خلص", en: "This offer has ended" },
                            ctaLabel: { ar: "استخدم كود", en: "Use code" },
                            ctaHref: "",
                            // A preset cannot ship a live discount code either — it would
                            // be a code that does not exist in the seller's store. Left
                            // empty, the button does not draw until the seller sets one.
                            couponCode: "",
                            image: null,
                            overlayOpacity: 0,
                            // MEASURED `--fsc-bg: #0F1A3C` with `--fsc-btn-bg: #FFF` — a
                            // saturated navy panel with a white pill, which is the accent
                            // band. Note this is the OPPOSITE of blast's identical-shaped
                            // card, whose `--to-bg` is a pale yellow taking `alt`.
                            cardSurface: "accent",
                        },
                    },
                    // #5 — `ab-pgrid` "Best Sellers", 8 cards in 4 columns under a
                    // centred title. MEASURED 909px at 1440.
                    primeRow(1, 0, "standard", 8, "center", true),
                    // #6 — `ab-comparison-section ab-comparison--square`. The class says
                    // square and the BOX says otherwise, for the FIFTH family: MEASURED
                    // media 1200x675 = 1.778, i.e. 16:9. Mapped to `wide`, exactly as
                    // dark's, volta's, hype's and blast's were.
                    // `--comp-max-width: 1200px` here, against blast's 1000.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "panel",
                            aspect: "wide",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "شوف الفرق", en: "See the transformation" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    // #7 — `ab-plist` "Business Books", a scrolling track with "View All"
                    // opposite a START-aligned title. MEASURED 430px.
                    primeRow(2, 1, "slider", 6, "start", true),
                    // #8 — `shop-the-look`, and NOT the layout avyro ported. prime ships
                    // the same section as a horizontal CAROUSEL of full-height look
                    // photographs, each with a pin badge that opens its products.
                    // MEASURED: slides a fixed 515px tall at >=900 and 345px below, at
                    // their own natural widths (687/426/517/773 at 1440); section 717px.
                    // The `look-carousel` variant ported for this family.
                    //
                    // Its `look` blocks are declared with NULL images, like every other
                    // image field in every preset. Consequence, stated plainly: the
                    // carousel renders EMPTY until the seller uploads look photos,
                    // because a look with no photograph is a blank 515px box and drops.
                    {
                        id: "shop-the-look-1",
                        type: "shop-the-look",
                        visible: true,
                        settings: {
                            variant: "look-carousel",
                            title: { ar: "استوحِ من المساحات", en: "Get inspired by spaces" },
                            headingLevel: 2,
                            surface: "page",
                        },
                        blocks: [
                            {
                                id: "look-1",
                                type: "look",
                                settings: {
                                    image: null,
                                    label: { ar: "الإطلالة الأولى", en: "First look" },
                                    productIds: "$products:4",
                                },
                            },
                            {
                                id: "look-2",
                                type: "look",
                                settings: {
                                    image: null,
                                    label: { ar: "الإطلالة التانية", en: "Second look" },
                                    productIds: "$products:4",
                                },
                            },
                            {
                                id: "look-3",
                                type: "look",
                                settings: {
                                    image: null,
                                    label: { ar: "الإطلالة التالتة", en: "Third look" },
                                    productIds: "$products:4",
                                },
                            },
                            {
                                id: "look-4",
                                type: "look",
                                settings: {
                                    image: null,
                                    label: { ar: "الإطلالة الرابعة", en: "Fourth look" },
                                    productIds: "$products:4",
                                },
                            },
                        ],
                    },
                    // #9 — `ab-plist` "Romance Books", the second carousel, identical to
                    // #7 on another category.
                    primeRow(3, 2, "slider", 6, "start", true),
                    // #10 — `rnw-hero rnw-hero--left` used MID-PAGE while the page's own
                    // hero is the same engine centred. MEASURED inline:
                    // `--rnw-h-dt: 65svh` (585), `--rnw-h-mb: 35svh` (339 at 390),
                    // `--rnw-overlay: 66`, `--rnw-b-rad: 5px`. The `banner-runway`
                    // variant volta ported — prime is the third family to need it.
                    //
                    // Note the overlay differs from blast's 83 and the mobile height
                    // from blast's 50svh: same engine, different authoring.
                    {
                        id: "banner-1",
                        type: "banner",
                        visible: true,
                        settings: {
                            variant: "banner-runway",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق التشكيلة", en: "Shop collection" },
                            ctaHref: "collections",
                            image: null,
                            overlayOpacity: 66,
                            textAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #11 — `ab-featured-products`: a scroller whose FIRST slide is a
                    // promo tile for the category itself, sized to the same track slot
                    // as every product beside it. This is `hero-split`, which
                    // `BuyerCategorySection` already documents as a port of prime's own
                    // `ab-featured-hero` — no new variant was needed.
                    //
                    // Its responsive ladder WAS wrong (it pinned the 1440 numbers at
                    // every width) and was re-measured and fixed with this family; see
                    // the plan doc.
                    primeRow(4, 3, "hero-split", 6, "start", true),
                    // #12 — `hr-reviews`: one quote at display scale on a coloured band,
                    // with the neighbouring quotes peeking either side at 0.22 opacity.
                    // MEASURED 434px at 1440, 325px at 768, 345px at 390, slides
                    // `min(605px, 42vw)`. The `test-stage` variant ported for this
                    // family.
                    //
                    // MEASURED `--hr-bg: #DADAD3` — a warm grey, i.e. a SUBTLE band, not
                    // the accent and not the page.
                    {
                        id: "testimonials-1",
                        type: "testimonials",
                        visible: true,
                        settings: {
                            variant: "test-stage",
                            surface: "alt",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "آراء عملائنا", en: "What clients talk about us" },
                            description: { ar: null, en: null },
                            headingLevel: 2,
                        },
                        blocks: [
                            {
                                id: "testimonial-1",
                                type: "testimonial",
                                settings: {
                                    quote: { ar: null, en: null },
                                    author: { ar: null, en: null },
                                    role: { ar: null, en: null },
                                    image: null,
                                    rating: 5,
                                },
                            },
                            {
                                id: "testimonial-2",
                                type: "testimonial",
                                settings: {
                                    quote: { ar: null, en: null },
                                    author: { ar: null, en: null },
                                    role: { ar: null, en: null },
                                    image: null,
                                    rating: 5,
                                },
                            },
                            {
                                id: "testimonial-3",
                                type: "testimonial",
                                settings: {
                                    quote: { ar: null, en: null },
                                    author: { ar: null, en: null },
                                    role: { ar: null, en: null },
                                    image: null,
                                    rating: 5,
                                },
                            },
                        ],
                    },
                    // #13 — `newsletter-luxe`, the centred variant nature mapped.
                    // MEASURED: their element authors `--nl-text` and `--nl-btn` as the
                    // navy but NO `--nl-bg`, so the band falls through to
                    // `var(--body-bg)` — WHITE. That makes prime the fourth different
                    // ground this one section has taken across the families built so
                    // far: hype's near-black, volta's accent, blast's pale `alt`, and
                    // prime's plain page.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-center",
                            surface: "page",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اشترك في نشرتنا", en: "Join our newsletter" },
                            subhead: {
                                ar: "اشترك واحصل على خصم 10% على أول طلب.",
                                en: "Sign up to our newsletter and receive 10% off your first order.",
                            },
                            buttonLabel: { ar: "اشترك", en: "Sign up" },
                            buttonStyle: "primary",
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // MEASURED `ab-footer--kids ab-header-wave` — prime's own template,
                    // and the archetype the footer survey named `footer-wave` for it:
                    // "its own template, a `top` block over a `bar` block". The survey
                    // originally grouped prime with phoenix as one subscribe-led family
                    // and flagged that pairing as its least certain; it was the one that
                    // split, and prime is why `footer-wave` exists.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-wave", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        id: "compact",
        name: { ar: "مُدمج", en: "Compact" },
        description: {
            ar: "خط هادئ في المنتصف على خلفيات رمادية مسطحة.",
            en: "Centred, quiet type on flat grey media wells.",
        },
        tokens: {
            colors: {
                // MEASURED on sharp-bags (2026-07-20): their h2 ink computes to
                // rgba(3,3,2,.76) over white, so the solid behind it is #030302.
                primary: "#030302",
                // WAS `#e32c2b` — the unmeasured sale red the avyro note flagged
                // across ten `eo-*` presets and the nature note corrected for a third
                // family. It appears NOWHERE on sharp's page. `accent` is a BAND
                // colour in our model: it grounds the header and every accent band,
                // `footer-directory` included. Sharp's band is a dark brown, measured
                // rgb(76,43,37) on BOTH the header and the footer, with white over it.
                accent: "#4c2b25",
                surface: "#ffffff",
                // MEASURED body ink rgb(19,19,22). Was #030302, which is the HEADING
                // colour — the same conflation the nature pass corrected.
                ink: "#131316",
            },
            // Re-verified at 1440: heading and body both compute to
            // `"Instrument Sans", sans-serif`. Already correct.
            typography: {
                heading: "Cairo",
                body: "Cairo",
                headingEn: "Instrument Sans",
                bodyEn: "Instrument Sans",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "System Serif",
            },
            radius: "0",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "0",
            pageGutterMobile: "0",
            // `showcase` is CORRECT and was already right — the card workstream
            // ported it FROM this family (`sharp` → `showcase`). Re-measured on
            // sharp-bags: 360×469 card, white ground, radius 0, no border, and a
            // 1:1 media plate (359×359).
            productCardStyle: "showcase",
            productCardRadius: "0",
        },
        // The FOURTH preset to carry page structure (2026-07-20, family 4 of 13).
        // Section order, variants and settings are the measured sharp-bags
        // homepage — see the "sharp" subsection of
        // docs/plans/2026-07-19-storefront-section-vocabulary.md.
        //
        // Conforms to the model `eo-avyro` settled and `eo-elite` / `eo-nature`
        // confirmed: same `TemplateInstance` shape, same `$category:N` slots, same
        // rules (no imagery ever, neutral placeholder copy because text is a
        // CONTENT field the seller's own copy is merged over on apply).
        template: {
            home: {
                sections: [
                    // MEASURED: sharp's header ground is rgb(76,43,37) — the same brown
                    // as the footer, i.e. this preset's accent. The header field's
                    // default IS the accent band, but it is pinned explicitly here so a
                    // later accent edit cannot silently repaint it.
                    {
                        id: "header",
                        type: "header",
                        locked: true,
                        visible: true,
                        settings: { variant: "luxe-centered", surface: "accent", transparentOverHero: true, mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false },
                    },
                    // #0 — `bottom-banner --under-nav`: the full-bleed photograph with
                    // the copy on the leading edge and an outlined CTA on the trailing
                    // edge. This is the `billboard` hero engine, ported for THIS family.
                    // MEASURED 720px from 768 up, 675 below.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "billboard",
                            // `upload` rather than `featured_product`: their band is art
                            // direction, not a product shot. Ships null like every preset
                            // image; the seller fills it.
                            mediaSource: "upload",
                            image: null,
                            showEyebrow: true,
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق الآن", en: "Shop now" },
                            ctaHref: "all_products",
                            // Their band carries ONE button. Without this the media
                            // pipeline's unconditional second CTA would appear.
                            ctaHrefSecondary: "none",
                            textAlign: "start",
                            overlayOpacity: 0,
                        },
                    },
                    // sharp ships NO trust strip. Omitting the section is not enough:
                    // `BuyerStoreHomepage` pins a SYNTHETIC value strip after the hero
                    // whenever the list contains no `value-strip` at all. The shim tests
                    // for the TYPE, not visibility — see the avyro assembly note.
                    {
                        id: "value-strip-1",
                        type: "value-strip",
                        visible: false,
                        settings: {},
                    },
                    // #1 — `ps-plist`: the horizontal product scroller. "Best Sellers".
                    sharpRow(1, 0, "slider"),
                    // #2 — `ab-categories-section`: the bento mosaic. SIX tiles on their
                    // page, alternating 773×722 and 647×351 — every third tile spanning
                    // two rows, which is exactly what `bento` was ported to do.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "bento",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "تسوق حسب المجموعة", en: "Shop by collection" },
                            // sharp folds the mosaic into a SIDE SCROLLER below 1024px where
                            // elite stacks it — MEASURED at 390px, `display:flex` snap
                            // scroller (section 331px) against elite's one-column grid
                            // (1618px). Left at the `stack` default this section rendered
                            // 1841px at 390 against their 331, a +456% overshoot that only
                            // the page-level diff exposed.
                            mobileLayout: "scroll",
                            headingLevel: 2,
                        },
                    },
                    // #3 — the first MID-PAGE `bottom-banner`. Same band as the hero,
                    // 45px shorter (675 / 630). It renders through `banner` rather than
                    // `hero` because `hero.maxPerTemplate` is 1 and this family ships
                    // three of them; see BillboardVariant.tsx.
                    {
                        id: "banner-1",
                        type: "banner",
                        visible: true,
                        settings: {
                            variant: "banner-billboard",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق الآن", en: "Shop now" },
                            ctaHref: "all_products",
                            image: null,
                            textAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #4 — `ps-pgrid-section --home`: the plain 4×2 grid.
                    sharpRow(2, 1, "standard"),
                    // #5 — `psc-compare --aspect-cinematic`. MEASURED viewport 1440×754
                    // at 1440 and 768×402 at 768 → ratio 1.909 at BOTH, which is our
                    // existing `panorama` (21/11 = 1.909). Their `cinematic` modifier
                    // has no rule in their stylesheet — same finding as nature, so no
                    // new aspect option is needed here either.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "panel",
                            aspect: "panorama",
                            eyebrow: { ar: "لو داليا", en: "Le Dalia" },
                            title: { ar: "اختر لونك", en: "Choose your colour" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    // #6 — `ps-featured`: grid whose first cell is a category banner.
                    sharpRow(3, 2, "banner-grid"),
                    // #7 — the second mid-page `bottom-banner`.
                    {
                        id: "banner-2",
                        type: "banner",
                        visible: true,
                        settings: {
                            variant: "banner-billboard",
                            eyebrow: { ar: null, en: null },
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            ctaLabel: { ar: "تسوق الآن", en: "Shop now" },
                            ctaHref: "all_products",
                            image: null,
                            textAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #8 — `ps-plist` again. "Clutches Bags".
                    sharpRow(4, 3, "slider"),
                    // #9 — `editorial-feature --light --img-left`. MEASURED media 4:5
                    // (549×686) with a 523×255 copy column at 1440 — identical geometry
                    // to avyro's, which is the instance `about-editorial` was ported
                    // from. `imagePosition: "start"` is their `--img-left`.
                    {
                        id: "about-1",
                        type: "about",
                        visible: true,
                        settings: {
                            variant: "about-editorial",
                            eyebrow: { ar: null, en: null },
                            title: { ar: null, en: null },
                            body: { ar: null, en: null },
                            image: null,
                            imagePosition: "start",
                            ctaLabel: { ar: null, en: null },
                            ctaHref: "",
                            headingLevel: 2,
                        },
                    },
                    // #10 — the last `ps-featured` row. "Crossbody Bags".
                    sharpRow(5, 4, "banner-grid"),
                    // #11 — `newsletter-immersive --h-comfortable`. MEASURED 522px at
                    // EVERY width from 390 to 1440, and their title is 44px/w700/
                    // ls 2.64px uppercase — which is exactly what our `comfortable`
                    // height and `news-immersive` type scale already produce.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-immersive",
                            eyebrow: { ar: "ابقَ على اطلاع", en: "Keep me updated" },
                            title: { ar: "النشرة البريدية", en: "Newsletter" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                            height: "comfortable",
                            overlayOpacity: 45,
                            image: null,
                            fineprint: { ar: null, en: null },
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // The `ps` grid footer — the archetype avyro/elite/nature/sharp
                    // share. The footer survey notes sharp is the member that ships NO
                    // first-cell block, so the grid settles at four columns.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-directory", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        // luxury — family 13 of 13, the LAST (2026-07-21). An `ab-*` editorial
        // fashion store: an `edition-hero-slider` WITH text, two `ab-pgrid` plain
        // grids, the shared `shop-the-look`/`editorial-feature`/`newsletter-luxe`
        // cream sections, an `ab-marquee` black band, and the light `ab-footer`
        // boutique. It shares the engine with pearl/peral/phoenix but NOT the page.
        //
        // Reference: https://luxury-fashion.myeasyorders.com. Capture
        // `scratchpad/fam-luxury.json` (settled, 10 sections — the re-measure
        // predicted exactly 10). Measurements `scratchpad/luxury-*.mjs`.
        //
        // luxury is the FIFTH family (with volta, pearl, peral, phoenix) to need NO
        // new section TYPE and the THIRD (with volta, phoenix) to need NO new
        // variant at all — every one of its ten sections maps to a variant an
        // earlier family already ported. The type vocabulary is closed; the last
        // family added nothing to it.
        id: "gilded",
        name: { ar: "مذهّب", en: "Gilded" },
        description: {
            ar: "أزياء تحريرية — كحلي هادئ وعناوين خفيفة.",
            en: "Editorial fashion — quiet navy and extra-light headings.",
        },
        tokens: {
            colors: {
                // MEASURED on luxury-fashion: the section titles (plist/pgrid/cat, 24px
                // and 48px w200), the "View All" link and the card frames all compute
                // `rgb(49,62,97)` = #313e61 — the extra-light NAVY that is luxury's
                // identity. `primary` drives exactly those, so it carries the navy.
                // CORRECTED from #1a1a1a (unmeasured): our titles would have read
                // near-black instead of navy. (The gold #c9a962 on the shop-the-look /
                // newsletter CTAs is EasyOrders' section-local accent, not the store's
                // primary — recorded as a departure below.)
                primary: "#313e61",
                // CORRECTED from the unmeasured sale-red #cd1515 — the same ninth-plus
                // catch every `ab-*` preset made. `accent` is a BAND colour; #cd1515 is
                // only the sale-badge red (`rgb(205,21,21)`) / discounted-price signal,
                // never a band. MEASURED, luxury's one true accent BAND is the
                // `ab-marquee` strip whose `.ab-marquee-track` computes pure
                // `rgb(0,0,0)`; set to the near-black the marquee grounds on (the
                // marquee section takes `surface: "accent"`, exactly as dark's does).
                // Applying the red would have painted the marquee band red.
                accent: "#0b0c10",
                // MEASURED: body ground rgb(255,255,255).
                surface: "#ffffff",
                // MEASURED: `.ab-plist-name` / `.ab-pgrid-name` body ink rgb(33,42,47) =
                // #212a2f. Also the dark "stone" the transparent `ab-header` grounds on
                // (header takes `surface: "ink"`, peral's resolution for the same
                // engine).
                ink: "#212a2f",
                // FIX PHASE B — the FOOTER BAND. MEASURED rgb(240,243,242) with BLACK
                // ink; ours rendered the derived `alt` pair (#F2F2F3 / #212A2F). Both
                // sides are light here, so this family is not one of the band-luminance
                // cases — it is a plain value correction.
                footerSurface: "#f0f3f2",
                footerInk: "#000000",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                // MEASURED: `Jost` computes on `.ab-plist-name`/`.ab-pgrid-name` (the
                // same engine as pearl/peral). Cairo for Arabic, consistent with every
                // family. The serif on the editorial/shop-the-look/newsletter titles is
                // those variants' own pinned face, not the store heading.
                headingEn: "Jost",
                bodyEn: "Jost",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face. The luxury audit found the HERO's
                // `ui-serif` is correct on BOTH sides; what fell back to Jost were the
                // editorial / shop-the-look / newsletter titles. The system-serif
                // sentinel is the same stack their hero already computes, so it fixes
                // those roles without disturbing the hero.
                displayEn: "System Serif",
            },
            // MEASURED: `.ab-pgrid-card` / `.ab-pgrid-media` compute radius 0.
            radius: "0",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. luxury was the worst case in the
            // audit: SIX insets on a single page (0/20/72/115/160/220) plus three
            // full-bleed bands, against their single 24px content_container applied
            // to every section INCLUDING the hero.
            pageGutter: "24px",
            pageGutterMobile: "16px",
            // VERIFIED against the measured card: the `editorial` (EditorialCard)
            // port, the `ab-*` engine card, radius 0 — identical to pearl/peral.
            productCardStyle: "editorial",
            productCardRadius: "0",
        },
        // The THIRTEENTH and final preset to carry page structure, conforming to
        // the `TemplateInstance` model avyro settled — same `$category:N` /
        // `$products:N` slots, same no-imagery / placeholder-copy rules. No second
        // shape invented.
        //
        // TWO categories, in the portrait RAIL order (Men, Women) — their page
        // ships exactly two `ab-cat-card`s (SHOP MEN, SHOP WOMEN):
        //   slot 0 Men   -> #1 New Arrivals (slider, curated) AND #4 Men's (grid)
        //   slot 1 Women -> #8 Women's (grid)
        template: {
            home: {
                sections: [
                    // MEASURED: luxury's `ab-header` computes `rgba(0,0,0,0)` (transparent,
                    // floating over the dark hero) with WHITE ink. Not expressible as a
                    // floating transparent bar; grounded on the ink band (dark stone,
                    // white text) — peral's resolution for the identical `ab-header`. The
                    // header field defaults to the ACCENT band (near-black here), so it is
                    // set explicitly.
                    { id: "header", type: "header", locked: true, visible: true, settings: { variant: "luxe-centered", surface: "ink", transparentOverHero: true, mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false } },
                    // Declared OFF: luxury ships no value strip, and a section the preset
                    // omits is merged back VISIBLE, plus `BuyerStoreHomepage` pins a
                    // SYNTHETIC strip after the hero when the list has no `value-strip`
                    // TYPE. Absent means "default", not "off". (avyro #4.)
                    { id: "value-strip-1", type: "value-strip", visible: false, settings: {} },
                    // #0 — `edition-hero-slider`: THREE crossfade slides, each carrying its
                    // own serif title (52px `rgb(250,247,242)`), lede and CTA over a dark
                    // shade — HAS text (unlike phoenix/peral's baked-artwork `ab-slider`).
                    // `edition`, the engine `EditionVariant.tsx` names. autoplayInterval is
                    // SECONDS (schema 2–10; their `data-autoplay` is ms). MEASURED 648px.
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "edition",
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            autoplayInterval: 6,
                            textAlign: "start",
                            overlayOpacity: 25,
                        },
                        blocks: [1, 2, 3].map((n) => ({
                            id: `hero-slide-${n}`,
                            type: "slide",
                            settings: {
                                image: null,
                                headline: { ar: null, en: null },
                                subhead: { ar: null, en: null },
                                ctaLabel: { ar: null, en: null },
                                ctaHref: "",
                            },
                        })),
                    },
                    // #1 — `ab-plist` "SHOP NEW ARRIVALS": a 6-card scroller, small
                    // navy title on the START edge with "View All" + prev/next arrows
                    // opposite. `category-sections`/`slider`, bound to slot 0. MEASURED
                    // title 24px w200 navy left = `plain` + `start` (the departure: plain
                    // is w400; none of the five treatments is extra-light — same call
                    // pearl/peral made). "New Arrivals" is a curated collection, so it
                    // keeps its own title while drawing Men products (placeholder).
                    {
                        id: "category-sections-1",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "slider",
                            categoryId: "$category:0",
                            title: { ar: "أحدث المنتجات", en: "New Arrivals" },
                            showSectionTitle: true,
                            showViewAll: true,
                            productsPerSection: 8,
                            titleStyle: "plain",
                            titleAlign: "start",
                            headingLevel: 2,
                        },
                    },
                    // #2 — `ab-categories`: TWO 4:5-ish portrait tiles (272×303 image, name
                    // BELOW in extra-light navy), "Shop by collection" 48px w200 centred
                    // above. `portrait` — `BuyerCollectionGrid` documents that variant as a
                    // port of "the EasyOrders `ab-cat-card` portrait row (luxury, pearl,
                    // peral)", i.e. of THIS family's own row. Renders the full category
                    // list, so it shows the two pills. NO `count` (its `showIf` scopes it
                    // to the grid variants and `portrait` gets the whole list).
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "portrait",
                            showSectionTitle: true,
                            title: { ar: "تسوق حسب المجموعة", en: "Shop by collection" },
                            eyebrow: { ar: null, en: null },
                            headingLevel: 2,
                        },
                    },
                    // #3 — `editorial-feature editorial-feature--light --img-left`:
                    // eyebrow-less serif title (`rgb(28,26,23)`), body and a text link over
                    // an image on the START side. `about`/`about-editorial`,
                    // `imagePosition: start`. MEASURED 802px at 1440.
                    {
                        id: "about-1",
                        type: "about",
                        visible: true,
                        settings: {
                            variant: "about-editorial",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "قصتنا", en: "Our story" },
                            body: { ar: null, en: null },
                            image: null,
                            imagePosition: "start",
                            ctaLabel: { ar: "اكتشف", en: "Discover" },
                            ctaHref: "",
                            headingLevel: 2,
                        },
                    },
                    // #4 — `ab-pgrid` "Men's Collections": a PLAIN five-column wrapping
                    // grid, 10 cards over two rows, a centred navy 24px w200 title above.
                    // NO promo/banner tile (MEASURED: the first grid child is a product
                    // card, `gridTemplateColumns` is five equal 268.8px tracks) — so
                    // `category-sections`/`standard`, NOT `banner-grid`. columnsPerRow 5,
                    // bound to slot 0.
                    {
                        id: "category-sections-2",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "standard",
                            categoryId: "$category:0",
                            title: { ar: null, en: null },
                            showSectionTitle: true,
                            columnsPerRow: 5,
                            productsPerSection: 10,
                            titleStyle: "plain",
                            titleAlign: "center",
                            headingLevel: 2,
                        },
                    },
                    // #5 — `ab-comparison-section ab-comparison--square`: the DRAG SLIDER
                    // (After/Before pill labels + VIEW PRODUCT CTAs), not the static
                    // compare panel. `before-after`/`slider`, `shape: square`. MEASURED
                    // ground rgb(249,249,249) off-white, so `alt`.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "slider",
                            shape: "square",
                            orientation: "horizontal",
                            surface: "alt",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "شوف الفرق", en: "See the Transformation" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    // #6 — `shop-the-look--cream` (already ported for avyro): gold eyebrow
                    // ("STYLING ROOM"), serif title, a left hero visual with a "Shop the
                    // look" pill and a right column of THREE linked product cards.
                    // `look-classic`. MEASURED 799px at 1440, ground rgb(243,239,232) warm
                    // off-white = `alt`.
                    {
                        id: "shop-the-look-1",
                        type: "shop-the-look",
                        visible: true,
                        settings: {
                            variant: "look-classic",
                            surface: "alt",
                            eyebrow: { ar: "غرفة التنسيق", en: "Styling room" },
                            title: { ar: "اشتري الإطلالة", en: "Shop the look" },
                            subtitle: { ar: null, en: null },
                            headingLevel: 2,
                            heroImage: null,
                            heroTitle: { ar: null, en: null },
                            heroCaption: { ar: null, en: null },
                            heroCtaLabel: { ar: "تسوق الإطلالة", en: "Shop the look" },
                            heroHref: "",
                            productIds: "$products:3",
                        },
                    },
                    // #7 — `ab-marquee-section` "LIMITED EDITION COLLECTION": a full-bleed
                    // black strip of 48px w700 uppercase white type scrolling sideways.
                    // `marquee-band`, `size: medium` (MEASURED item 48px, section 68px =
                    // the medium line-height), `textCase: upper`. `surface: "accent"` —
                    // the near-black band token, exactly dark's call; without it the
                    // section inherits `--section-bg: var(--paper-2)` from the wrapper and
                    // renders black-on-white. MEASURED `.ab-marquee-track` = rgb(0,0,0).
                    {
                        id: "marquee-band-1",
                        type: "marquee-band",
                        visible: true,
                        settings: {
                            surface: "accent",
                            size: "medium",
                            speed: "medium",
                            direction: "start",
                            tilt: "none",
                            separator: "dot",
                            textCase: "upper",
                        },
                        blocks: [
                            {
                                id: "marquee-line-1-1",
                                type: "marquee-line",
                                settings: { text: { ar: null, en: null } },
                            },
                        ],
                    },
                    // #8 — `ab-pgrid` "Women's Collections": second PLAIN five-column grid,
                    // identical engine to #4, bound to slot 1.
                    {
                        id: "category-sections-3",
                        type: "category-sections",
                        visible: true,
                        settings: {
                            variant: "standard",
                            categoryId: "$category:1",
                            title: { ar: null, en: null },
                            showSectionTitle: true,
                            columnsPerRow: 5,
                            productsPerSection: 10,
                            titleStyle: "plain",
                            titleAlign: "center",
                            headingLevel: 2,
                        },
                    },
                    // #9 — `newsletter-luxe`: a centred column, serif title, input + gold
                    // submit, fineprint. `news-center`, the mapping nature/blast/peral/
                    // phoenix settled. MEASURED 348px at 1440, ground rgb(241,242,242).
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-center",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اشترك في نشرتنا", en: "Subscribe to our newsletter" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                            image: null,
                            fineprint: { ar: null, en: null },
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // `ab-footer` — the two-column boutique archetype the footer survey
                    // assigned to luxury/pearl/peral (`footer-boutique`, the store the
                    // shared variant was built FOR). Shop/Pages columns start, subscribe
                    // end, contact below, logo + socials end, payments centred. MEASURED
                    // the LIGHT ground rgb(240,243,242) = #f0f3f2, which the variant paints
                    // via its own `alt` pair.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-boutique", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
    {
        id: "editorial",
        name: { ar: "تحريري", en: "Editorial" },
        description: {
            ar: "تخطيط إيليت بلوحة ألوان محايدة.",
            en: "The Elite layout on a neutral palette.",
        },
        tokens: {
            colors: {
                primary: "#1c1c1c",
                // MEASURED on avyro-elite (2026-07-20), not their sale-price red.
                // `accent` is a BAND colour in our token model — it grounds the header
                // and every accent-band section including `footer-directory`. The
                // previous `#e32c2b` was EasyOrders' discount/strike red, which appears
                // on their page only as price ink; applying it as the band painted our
                // header and footer bright red while theirs are rgba(255,255,255,.96)
                // and rgb(28,28,28). Their footer ground is the value used here.
                // The other 10 `eo-*` presets still carry an unmeasured red accent
                // (#e32c2b / #cd1515 / #ff0000 / #c94a4a) and need the same check.
                accent: "#1c1c1c",
                surface: "#ffffff",
                ink: "#1c1c1c",
                // FIX PHASE A — the PAGE GROUND, distinct from `surface`. Their
                // content_container paints this and every section is transparent over
                // it, so cards/photos read as raised panels. Only 5 of 12 families
                // have a non-white ground; the other 7 deliberately omit this token.
                pageGround: "#EFEFEF",
            },
            typography: {
                heading: "Cairo",
                body: "Cairo",
                headingEn: "Instrument Sans",
                bodyEn: "Instrument Sans",
                headingAr: "Cairo",
                bodyAr: "Cairo",
                // FIX PHASE A — the DISPLAY face (hero titles + editorial H2s). Before this
                // token those headings hard-coded a `ui-serif` stack that loads no webfont,
                // so the family display face never rendered.
                displayEn: "System Serif",
            },
            radius: "0",
            logoTreatment: "none",
            // FIX PHASE A — ONE page container. MEASURED `content_container` inset
            // on the source store; replaces the per-section 0/20/32/72 hard-codes.
            pageGutter: "32px",
            pageGutterMobile: "18px",
            // `bordered`, NOT `card`. `BorderedCard` is documented in its own header
            // as "a faithful port of the EasyOrders Phoenix Avyro product card"
            // (`ab-pgrid-card.ab-featured-card`) — i.e. THIS family's card, ported in
            // the earlier card-vocabulary workstream and then not wired to this
            // preset. `card` has 1/1 media where theirs is 4/5, which made every
            // product row 25–40% shorter than theirs; the page-level height diff is
            // what surfaced it, since a square card looks perfectly fine on its own.
            productCardStyle: "bordered",
            productCardRadius: "14px",
        },
        // The FIRST preset to carry page structure (2026-07-20). Section order,
        // variants and settings are the measured avyro/elite homepage — see the
        // "avyro" subsection of
        // docs/plans/2026-07-19-storefront-section-vocabulary.md.
        //
        // Copy here is NEUTRAL PLACEHOLDER, deliberately not their marketing prose.
        // Text is a CONTENT field in the platform's content/styling split
        // (`CONTENT_FIELD_TYPES` in merge.ts), so a theme has no business
        // authoring it: on apply the seller's own copy is carried over this. Same
        // reason every image field below is null — `applyThemeToStore` blanks
        // theme images anyway.
        template: {
            home: {
                sections: [
                    // `surface: "page"` is MEASURED, not a default: avyro's header is
                    // rgba(255,255,255,.96) over the page ground. Our header field
                    // defaults to the ACCENT band (`sections/header.ts`), and this
                    // preset's accent is their footer near-black — so leaving it unset
                    // paints a black header under a white page. This is the one header
                    // setting the theme's identity depends on.
                    {
                        id: "header",
                        type: "header",
                        locked: true,
                        visible: true,
                        settings: { variant: "luxe-centered", surface: "page", transparentOverHero: true, mobileShowLogo: true, mobileShowMenu: true, mobileShowSearch: false },
                    },
                    // #0 — `ps-slider`: a 4-slide peek carousel, each slide pure artwork
                    // linking to a collection (their slides carry no text at all).
                    {
                        id: "hero-1",
                        type: "hero",
                        visible: true,
                        settings: {
                            variant: "spotlight",
                            headline: { ar: null, en: null },
                            subhead: { ar: null, en: null },
                            // SECONDS, not milliseconds — the schema's range is 2–10 and the
                            // validator rejects anything else. Their `data-autoplay="5000"`
                            // is in ms; copying that number straight across fails the save
                            // with `out_of_range`.
                            autoplayInterval: 5,
                            textAlign: "start",
                            overlayOpacity: 0,
                        },
                        blocks: [1, 2, 3, 4].map((n) => ({
                            id: `hero-slide-${n}`,
                            type: "slide",
                            settings: {
                                image: null,
                                headline: { ar: null, en: null },
                                subhead: { ar: null, en: null },
                                ctaLabel: { ar: null, en: null },
                                ctaHref: "",
                            },
                        })),
                    },
                    // avyro ships NO trust strip, and omitting the section is not enough
                    // to get that: `BuyerStoreHomepage` pins a SYNTHETIC value strip
                    // after the hero whenever the section list contains no `value-strip`
                    // at all (a compat shim for stores whose published theme predates the
                    // section). The shim tests for the TYPE, not for visibility, so a
                    // section that is present and switched off suppresses it — which is
                    // also the better seller default: the strip is one toggle away rather
                    // than absent and unexplained.
                    {
                        id: "value-strip-1",
                        type: "value-strip",
                        visible: false,
                        settings: {},
                    },
                    // #1 — `ab-categories-section`: the bento mosaic under a kicker.
                    {
                        id: "collections-1",
                        type: "collections",
                        visible: true,
                        settings: {
                            variant: "bento",
                            eyebrow: { ar: "أفضل المجموعات", en: "Best seller collections" },
                            title: { ar: "تسوق حسب الفئة", en: "Shop by category" },
                            headingLevel: 2,
                        },
                    },
                    // #2 / #4 / #6 / #8 — four `ps-featured` rows, one per category,
                    // interleaved with the three editorial breaks. The interleave IS the
                    // layout: a store with fewer categories drops the trailing rows and
                    // keeps the breaks (see `bindPresetTemplates`).
                    categoryRow(1, 0, { variant: "slider", columnsPerRow: 3 }),
                    // #3 — `psc-compare`, the compare panel.
                    {
                        id: "before-after-1",
                        type: "before-after",
                        visible: true,
                        settings: {
                            variant: "panel",
                            aspect: "ultrawide",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "اختر لونك", en: "Choose your colour" },
                            beforeImage: null,
                            afterImage: null,
                            showLabels: true,
                            beforeLabel: { ar: "قبل", en: "Before" },
                            afterLabel: { ar: "بعد", en: "After" },
                            initialPosition: 50,
                        },
                    },
                    categoryRow(2, 1, { variant: "banner-grid", columnsPerRow: 4 }),
                    // #5 — `shop-the-look`: one styled photograph beside the pieces.
                    {
                        id: "shop-the-look-1",
                        type: "shop-the-look",
                        visible: true,
                        settings: {
                            eyebrow: { ar: "غرفة التنسيق", en: "Styling room" },
                            title: { ar: "اشتري الإطلالة", en: "Shop the look" },
                            subtitle: { ar: null, en: null },
                            headingLevel: 2,
                            heroImage: null,
                            heroTitle: { ar: null, en: null },
                            heroCaption: { ar: null, en: null },
                            heroCtaLabel: { ar: "تسوق الإطلالة", en: "Shop the look" },
                            heroHref: "",
                            // Seeded from the catalogue so the section is not born empty;
                            // the seller re-picks. Degrades to fewer cards, never drops.
                            productIds: "$products:4",
                        },
                    },
                    categoryRow(3, 2, { variant: "slider", columnsPerRow: 2 }),
                    // #7 — `editorial-feature`, image on the leading edge.
                    {
                        id: "about-1",
                        type: "about",
                        visible: true,
                        settings: {
                            variant: "about-editorial",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "قصتنا", en: "Our story" },
                            body: { ar: null, en: null },
                            image: null,
                            imagePosition: "start",
                            ctaLabel: { ar: null, en: null },
                            ctaHref: "",
                            headingLevel: 2,
                        },
                    },
                    categoryRow(4, 3, { variant: "standard", columnsPerRow: 4 }),
                    // #9 — `newsletter-immersive`: the form over a full-bleed photo.
                    {
                        id: "newsletter-1",
                        type: "newsletter",
                        visible: true,
                        settings: {
                            variant: "news-immersive",
                            eyebrow: { ar: null, en: null },
                            title: { ar: "النشرة البريدية", en: "Newsletter" },
                            subhead: { ar: null, en: null },
                            buttonLabel: { ar: "اشترك", en: "Subscribe" },
                            buttonStyle: "primary",
                            height: "comfortable",
                            overlayOpacity: 45,
                            image: null,
                            fineprint: { ar: null, en: null },
                        },
                    },
                    { id: "nav-1", type: "nav", locked: true, visible: true, settings: {} },
                    // The `ps` grid footer — the archetype avyro/elite/nature/sharp share.
                    {
                        id: "footer",
                        type: "footer",
                        locked: true,
                        visible: true,
                        settings: { variant: "footer-directory", showPagesColumn: true, showContactColumn: true, splitBottomBar: true },
                    },
                ],
            },
        },
    },
];
/**
 * The scroll-reveal ENGINE each EasyOrders family runs, from the animation audit
 * (docs/plans/2026-07-19-storefront-section-vocabulary.md, "Two reveal engines"):
 * - `cycle`   — the `ps-*` engine (avyro, elite, nature, sharp): sections cycle
 *   through varied entrance motions.
 * - `uniform` — the `ab-*` engine (the other nine): one shared fade-up per
 *   section.
 * Applying one of these presets turns the theme-level `sectionReveal` ON with
 * the family's engine, so the storefront animates like its EasyOrders source.
 * Presets not listed here (the platform token-only presets) stay static.
 */
const EO_REVEAL_ENGINE = {
    "eo-avyro": "cycle",
    "eo-elite": "cycle",
    "eo-nature": "cycle",
    "eo-sharp": "cycle",
    "eo-dark": "uniform",
    "eo-hype": "uniform",
    "eo-blast": "uniform",
    "eo-prime": "uniform",
    "eo-volta": "uniform",
    "eo-pearl": "uniform",
    "eo-peral": "uniform",
    "eo-phoenix": "uniform",
    "eo-luxury": "uniform",
};
/**
 * The COLOUR PALETTE each family preset ships — kept in sync with its live
 * `demo-<family>` reference store (the fidelity source of truth). Applying a
 * family theme-colour must reproduce that store's accent / primary / ground /
 * ink exactly, so these are transcribed from the demo stores rather than
 * re-guessed in the token blocks above (which had drifted). One default preset
 * + these 13 families = the 14 theme colours the picker offers.
 */
const FAMILY_COLORS = {
    blush: { ink: "#131316", accent: "#101010", primary: "#111111", surface: "#ffffff", footerHeading: "#CF5E61", footerSurface: "#eed4cd" },
    // Accent shares Impact's red so the two navy/red families read as one palette.
    catalog: { ink: "#131316", accent: "#DF0112", primary: "#0f1a3c", surface: "#ffffff", footerSurface: "#0f1a3c" },
    compact: { ink: "#131316", accent: "#4C2B25", primary: "#030302", surface: "#ffffff" },
    display: { ink: "#131316", accent: "#212A2F", primary: "#0a193d", surface: "#ffffff", footerSurface: "#0f1a3c" },
    editorial: { ink: "#1c1c1c", accent: "#000000", primary: "#1c1c1c", surface: "#ffffff" },
    gallery: { ink: "#131316", accent: "#2B365B", primary: "#c9a962", surface: "#ffffff", footerSurface: "#222222" },
    gilded: { ink: "#212a2f", accent: "#2B365B", primary: "#313e61", surface: "#ffffff", footerInk: "#000000", footerSurface: "#f0f3f2" },
    grand: { ink: "#151515", accent: "#E60012", primary: "#000000", surface: "#ffffff", footerSurface: "#1f1f1f" },
    impact: { ink: "#131316", accent: "#DF0112", primary: "#0f1a3c", surface: "#ffffff", pageGround: "#0B0C10", footerSurface: "#0B0C10", headerSurface: "#13151C" },
    noir: { ink: "#151515", accent: "#C6FF34", primary: "#c6ff34", surface: "#ffffff", pageGround: "#0B0C10", footerHeading: "#C6FF34", footerSurface: "#13151c" },
    polished: { ink: "#131316", accent: "#2B365B", primary: "#c9a962", surface: "#ffffff", footerSurface: "#000000" },
    rounded: { ink: "#212a2f", accent: "#212A2F", primary: "#212a2f", surface: "#f5f4f2", pageGround: "#ECE9E2", footerSurface: "#212121", headerSurface: "#ffffff" },
    warm: { ink: "#1c1c1c", accent: "#000000", primary: "#c9a962", surface: "#ffffff", footerInk: "#FEE6CE", pageGround: "#EFEFEF", footerSurface: "#7e451d" },
};
/**
 * The exported presets, with each family's palette synced to its demo store
 * ({@link FAMILY_COLORS}) and the scroll-reveal driver layered onto the `eo-*`
 * families from {@link EO_REVEAL_ENGINE}. Kept as a mapping over the raw list so
 * both tables stay the single, auditable source of truth.
 */
export const themePresets = RAW_THEME_PRESETS.map((preset) => {
    const colors = FAMILY_COLORS[preset.id];
    const engine = EO_REVEAL_ENGINE[preset.id];
    if (!colors && !engine)
        return preset;
    return {
        ...preset,
        tokens: {
            ...preset.tokens,
            ...(colors ? { colors } : {}),
            ...(engine
                ? {
                    sectionReveal: true,
                    sectionRevealStyle: engine,
                    sectionRevealSpeed: 600,
                }
                : {}),
        },
    };
});
export function findPresetById(id) {
    return themePresets.find((p) => p.id === id);
}
//# sourceMappingURL=presets.js.map